/**
 * Traffic Analytics Page
 * 
 * Displays traffic analytics from BigQuery/Segment data.
 * Shows source category breakdown, daily trends, and drill-down capability.
 */

import { useState, useEffect, useMemo } from 'react'
import { channelColor, resolveChannel } from '../lib/channels'
import { useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, LineChart, Line, ReferenceLine
} from 'recharts'
import { trafficAPI, marketingAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import ReportToolbar from '../components/ReportToolbar'
import { scopeOptions, scopeParams, scopeLabel, contentOptionsFor } from '../lib/trafficScope'
import SearchMultiSelect from '../components/SearchMultiSelect'
import PaymentFreshnessStrip from '../components/PaymentFreshnessStrip'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import { sortTrafficTree } from '../lib/trafficTreeSort'
import { buildSourceTree, decodeUTM } from '../lib/sourceTree'
import SourceTreeNode, { TrafficTreeSortHeaders } from '../components/SourceTreeNode'
import { FUNNEL_STEPS, funnelStepView } from '../lib/funnelSteps'
import { biggestLeakStep, formatCurrentThrough, formatDayRange, formatDuration, formatPct, screenLabel, screenLifetimeNote } from '../lib/intakeScreenSteps'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Users, Eye, TrendingUp,
  ChevronDown, ChevronRight, Globe, Zap, Search, ArrowLeft, Mail,
  FileText, Settings, Plus, Trash2, Save, X, Copy, Check, Clock, ListOrdered
} from 'lucide-react'

// Format large numbers
function formatNumber(value) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value?.toLocaleString() || '0'
}

// ── Traffic quality score helpers ────────────────────────────────────
// Shallow (bounced) → deep (paid). Same order as MILESTONES on the backend.
const DEPTH_COLORS = {
  lp_only: '#EF4444',   // bounced off the LP
  browsed: '#F97316',
  intake: '#F59E0B',
  email: '#84CC16',
  plan: '#22C55E',
  pay_init: '#10B981',
  paid: '#059669',      // paid
}

const SCORE_LINE_COLORS = [
  '#6366F1', '#3B82F6', '#22C55E', '#F59E0B', '#EC4899', '#14B8A6', '#8B5CF6',
]

// quality_index is rebased so 100 = brand average for the window.
function indexTone(index) {
  if (index >= 125) return 'text-emerald-600'
  if (index >= 100) return 'text-green-600'
  if (index >= 75) return 'text-amber-600'
  return 'text-red-500'
}

// Horizontal stacked bar showing what share of a group's sessions stopped at
// each depth. This is the "how deep does this traffic go" picture.
function DepthMixBar({ mix, milestones }) {
  if (!mix || !milestones?.length) return null
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-sm bg-muted">
      {milestones.map(m => {
        const pct = mix[m.key] || 0
        if (pct <= 0) return null
        return (
          <div
            key={m.key}
            className="h-full"
            style={{ width: `${pct}%`, backgroundColor: DEPTH_COLORS[m.key] }}
            title={`${m.label}: ${pct.toFixed(1)}%`}
          />
        )
      })}
    </div>
  )
}

// Get default date range (last 30 days)
function getDefaultDateRange() {
  const end = new Date()
  const start = subDays(end, 29)
  return { from: start, to: end }
}

// Custom tooltip for charts
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm z-50">
        <p className="font-medium text-popover-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">{formatNumber(entry.value)}</span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

// Custom donut chart tooltip
function PieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 text-sm z-50 relative">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: data.payload.color }}
          />
          <span className="font-semibold">{data.payload.name}</span>
        </div>
        <div className="text-lg font-bold">{formatNumber(data.value)}</div>
        <div className="text-xs text-muted-foreground">
          {((data.value / data.payload.total) * 100).toFixed(1)}% of total
        </div>
      </div>
    )
  }
  return null
}

// Recursive tree node component for pages
function PageTreeNode({ node, depth, expandedPaths, togglePath, formatNumber }) {
  const isExpanded = expandedPaths.has(node.path)
  const hasChildren = node.hasChildren
  
  // Determine icon based on node type
  const getIcon = () => {
    if (node.isHostname) return '🌐' // Globe for subdomain/hostname
    if (hasChildren) return isExpanded ? '📂' : '📁' // Open/closed folder emoji for directories
    return '📄' // Page emoji for leaf pages
  }
  
  return (
    <>
      <div 
        className={`grid grid-cols-[1fr_80px_80px_80px_80px_80px] gap-2 py-1.5 text-sm hover:bg-muted/30 rounded cursor-pointer transition-colors ${
          depth === 0 ? 'border-b border-muted/30' : ''
        }`}
        onClick={() => hasChildren && togglePath(node.path)}
      >
        <div 
          className="flex items-center gap-1 min-w-0"
          style={{ paddingLeft: `${depth * 16}px` }}
        >
          {hasChildren ? (
            <button 
              className="p-0.5 hover:bg-muted rounded shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                togglePath(node.path)
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          
          <span className="shrink-0">{getIcon()}</span>
          
          <span 
            className={`text-xs truncate ${node.isHostname ? 'font-semibold' : 'font-mono'}`}
            title={node.fullPath || node.path}
          >
            {node.name}
          </span>
        </div>
        <div className="text-right text-muted-foreground">{formatNumber(node.pageviews)}</div>
        <div className="text-right">{formatNumber(node.visitors)}</div>
        <div className="text-right text-muted-foreground">{formatNumber(node.emails)}</div>
        <div className="text-right">{formatNumber(node.payments)}</div>
        <div 
          className="text-right font-medium"
          style={{ 
            color: node.cvr >= 10 ? '#10B981' : node.cvr >= 5 ? '#F59E0B' : '#6B7280'
          }}
        >
          {node.cvr?.toFixed(2) || 0}%
        </div>
      </div>
      
      {/* Render children if expanded */}
      {isExpanded && hasChildren && (
        <div className="border-l border-muted/50 ml-2">
          {node.children.map((child) => (
            <PageTreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              togglePath={togglePath}
              formatNumber={formatNumber}
            />
          ))}
        </div>
      )}
    </>
  )
}

// Summary card component
function DeltaBadge({ current, prior }) {
  if (!prior || prior === 0) return null
  const pct = ((current - prior) / prior) * 100
  if (!isFinite(pct)) return null
  const isPositive = pct > 0
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
    </span>
  )
}

function SummaryCard({ title, value, subtitle, icon: Icon, loading, color, delta }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
                  <Icon className="h-4 w-4" style={{ color }} />
                </div>
              )}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
              {delta && <DeltaBadge current={delta.current} prior={delta.prior} />}
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Copy to clipboard button component
function CopyForAIButton({ getText, className }) {
  const [copied, setCopied] = useState(false)
  
  const handleCopy = async () => {
    try {
      const text = getText()
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className={className}
    >
      {copied ? (
        <>
          <Check className="h-4 w-4 mr-1 text-green-500" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-4 w-4 mr-1" />
          Copy for AI
        </>
      )}
    </Button>
  )
}

export default function TrafficPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  // Which funnel step the per-source %s are measured against; null = step-over-step.
  const [funnelBase, setFunnelBase] = useState(null)
  const [data, setData] = useState(null)
  const [sources, setSources] = useState(null)
  const [funnelData, setFunnelData] = useState(null)
  const [dailyFunnelData, setDailyFunnelData] = useState(null)
  const [subSourceData, setSubSourceData] = useState(null)
  const [todayData, setTodayData] = useState(null)
  const [campaignList, setCampaignList] = useState(null)
  const [drilldownData, setDrilldownData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [funnelLoading, setFunnelLoading] = useState(true)
  const [subSourceLoading, setSubSourceLoading] = useState(false)
  const [todayLoading, setTodayLoading] = useState(true)
  const [drilldownLoading, setDrilldownLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [expandedCategory, setExpandedCategory] = useState(null)
  const [activeTab, setActiveTab] = useState('traffic') // 'traffic', 'funnel', or 'drilldown'
  // Source ids; none chosen is all traffic.
  const [sourceFilter, setSourceFilter] = useState([])
  // Campaign and content LABELS under the chosen sources — see lib/trafficScope.
  const [campaignFilter, setCampaignFilter] = useState([])
  const [contentFilter, setContentFilter] = useState([])
  // The chosen source's tree rows from before a campaign was picked, so the
  // campaign list does not shrink to the one campaign you chose.
  const [scopeOptionRows, setScopeOptionRows] = useState(null)
  const [showSourceInfo, setShowSourceInfo] = useState(null)
  const [selectedCampaign, setSelectedCampaign] = useState(null) // { source, sub_source: normalized utm_campaign, display_name }
  const [campaignSearch, setCampaignSearch] = useState('')
  const [pageData, setPageData] = useState(null)
  const [pageLoading, setPageLoading] = useState(false)
  const [expandedPaths, setExpandedPaths] = useState(new Set(['/'])) // Start with root expanded
  const [expandedSources, setExpandedSources] = useState(new Set()) // For source tree
  const [pageSort, setPageSort] = useState({ column: 'pageviews', direction: 'desc' })
  const [sourceSort, setSourceSort] = useState({ column: 'visitors', direction: 'desc' })
  const [aliasData, setAliasData] = useState(null)
  const [editingAlias, setEditingAlias] = useState(null)
  const [newAliasName, setNewAliasName] = useState('')
  const [newAliasGroup, setNewAliasGroup] = useState('')
  const [qualityData, setQualityData] = useState(null)
  const [qualityLoading, setQualityLoading] = useState(false)
  const [expandedQualitySources, setExpandedQualitySources] = useState(new Set())
  const [expandedQualitySubSources, setExpandedQualitySubSources] = useState(new Set())
  const [qualitySortColumn, setQualitySortColumn] = useState('unique_sessions')
  const [qualitySortDirection, setQualitySortDirection] = useState('desc')
  const [scoreData, setScoreData] = useState(null)
  const [scoreTrend, setScoreTrend] = useState(null)
  const [scoreLoading, setScoreLoading] = useState(false)
  const [scoreGroupBy, setScoreGroupBy] = useState('source') // 'source' | 'sub_source' | 'landing_page'
  const [scoreInterval, setScoreInterval] = useState('day') // 'day' | 'week'
  const [showScoreWeights, setShowScoreWeights] = useState(false)

  // Depth levels this brand actually distinguishes. A level nobody stops at
  // (e.g. TMates fires email_entered and plan_selected on the same screen) is
  // 0% of every mix bar, so drawing it just adds a dead legend entry.
  const visibleDepths = useMemo(
    () => (scoreData?.weights || []).filter(w => w.observed),
    [scoreData]
  )
  const [durationData, setDurationData] = useState(null)
  const [durationLoading, setDurationLoading] = useState(false)
  const [screenSteps, setScreenSteps] = useState(null)
  const [screenStepsLoading, setScreenStepsLoading] = useState(false)
  const [screenStepsError, setScreenStepsError] = useState(null)
  const [screenFunnel, setScreenFunnel] = useState(null) // null = the brand's biggest funnel
  const [durationPercentile, setDurationPercentile] = useState('p50') // 'p50' or 'p90'
  const [marketingDaily, setMarketingDaily] = useState(null) // daily spend from marketing API

  const { currentClinicId, user } = useAuthStore()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  
  // Parse date range from URL or use default
  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    
    if (startParam && endParam) {
      try {
        const from = parse(startParam, 'yyyy-MM-dd', new Date())
        const to = parse(endParam, 'yyyy-MM-dd', new Date())
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
          return { from, to }
        }
      } catch (e) {
        console.error('[TRAFFIC] Error parsing dates:', e)
      }
    }
    return getDefaultDateRange()
  }, [searchParams])
  
  // Update URL when date range changes
  const setDateRange = (newRange) => {
    if (newRange?.from && newRange?.to) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev)
        params.set('start', format(newRange.from, 'yyyy-MM-dd'))
        params.set('end', format(newRange.to, 'yyyy-MM-dd'))
        return params
      })
    }
  }
  
  // The filter as the API takes it: every section below the toolbar is
  // narrowed to the visitors whose first touch falls in it.
  const scopeOpts = useMemo(() => scopeOptions(scopeOptionRows, sourceFilter), [scopeOptionRows, sourceFilter])
  const contentOpts = useMemo(() => contentOptionsFor(scopeOpts, campaignFilter), [scopeOpts, campaignFilter])
  const scope = useMemo(
    () => scopeParams({ sources: sourceFilter, campaigns: campaignFilter, contents: contentFilter }, scopeOpts),
    [sourceFilter, campaignFilter, contentFilter, scopeOpts]
  )
  const scopeKey = JSON.stringify(scope)
  // The sections and tabs whose endpoints take one source only.
  const singleSource = sourceFilter.length === 1 ? sourceFilter[0] : null

  // Fetch traffic sources config
  useEffect(() => {
    async function fetchSources() {
      try {
        const response = await trafficAPI.getSources()
        if (response.data?.success) {
          setSources(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching sources:', err)
      }
    }
    fetchSources()
  }, [])
  
  // Fetch today's real-time data
  useEffect(() => {
    async function fetchToday() {
      try {
        setTodayLoading(true)
        const response = await trafficAPI.getToday()
        if (response.data?.success) {
          setTodayData(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching today data:', err)
      } finally {
        setTodayLoading(false)
      }
    }
    fetchToday()
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchToday, 60000)
    return () => clearInterval(interval)
  }, [currentClinicId])
  
  // Fetch traffic data
  useEffect(() => {
    async function fetchData() {
      if (!dateRange?.from || !dateRange?.to) return
      
      try {
        setLoading(true)
        setFunnelLoading(true)
        setError(null)
        
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')
        
        console.log('[TRAFFIC] Fetching data for', start, 'to', end)
        
        // Fetch traffic summary, funnel data, and marketing daily spend in parallel
        const [summaryRes, funnelRes, dailyFunnelRes, subSourceRes, marketingRes] = await Promise.all([
          trafficAPI.getSummary({ start, end, ...scope }),
          trafficAPI.getFunnel({ start, end, ...scope }),
          trafficAPI.getDailyFunnel({ start, end, ...scope }),
          trafficAPI.getSubSourceFunnel({ start, end, ...scope }),
          marketingAPI.getSpend({ startDate: start, endDate: end }).catch(() => null),
        ])
        
        if (summaryRes.data?.success) {
          setData(summaryRes.data.data)
          console.log('[TRAFFIC] Summary loaded:', summaryRes.data.data)
        } else {
          throw new Error(summaryRes.data?.error?.message || 'Failed to load traffic data')
        }
        
        if (funnelRes.data?.success) {
          setFunnelData(funnelRes.data.data)
          console.log('[TRAFFIC] Funnel loaded:', funnelRes.data.data)
        }
        
        if (dailyFunnelRes.data?.success) {
          setDailyFunnelData(dailyFunnelRes.data.data)
          console.log('[TRAFFIC] Daily funnel loaded:', dailyFunnelRes.data.data)
        }
        
        if (subSourceRes.data?.success) {
          setSubSourceData(subSourceRes.data.data)
          // The campaign and content options, from rows not yet narrowed by a campaign.
          if (!scope.campaign) setScopeOptionRows(subSourceRes.data.data?.subSources || [])
          console.log('[TRAFFIC] Sub-source loaded:', subSourceRes.data.data)
        }

        // Store marketing daily spend data for efficiency chart
        if (marketingRes?.data?.daily) {
          setMarketingDaily(marketingRes.data.daily)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error:', err)
        setError(err.response?.data?.error?.message || err.message)
      } finally {
        setLoading(false)
        setFunnelLoading(false)
        setIsRefreshing(false)
      }
    }
    
    fetchData()
    // scopeKey stands for `scope`, which is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, currentClinicId, scopeKey])

  // Changing the sources starts the campaigns over (their options are re-read
  // for the new sources, before any campaign narrows them), and another
  // clinic's campaign names mean nothing here. Content keeps only what the
  // chosen campaigns still offer.
  useEffect(() => {
    setCampaignFilter([])
    setContentFilter([])
  }, [sourceFilter, currentClinicId])
  useEffect(() => {
    setContentFilter(chosen => {
      const kept = chosen.filter(label => contentOpts.some(option => option.label === label))
      return kept.length === chosen.length ? chosen : kept
    })
  }, [contentOpts])

  // Drop sources the current clinic cannot have. Affiliate networks are
  // contracted per brand, so switching from MDExam to a brand without them would
  // otherwise leave "Everflow" selected and every panel filtered to nothing.
  useEffect(() => {
    const available = sources?.categories
    if (!available || !sourceFilter.length) return
    const kept = sourceFilter.filter(id => available.some(cat => cat.id === id))
    if (kept.length !== sourceFilter.length) setSourceFilter(kept)
  }, [sources, sourceFilter])

  // Fetch campaign list when drilldown tab is active
  useEffect(() => {
    async function fetchCampaigns() {
      if (activeTab !== 'drilldown' || !dateRange?.from || !dateRange?.to) return
      
      try {
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')
        
        const params = { start, end }
        if (singleSource) params.source = singleSource

        const response = await trafficAPI.getCampaigns(params)
        if (response.data?.success) {
          setCampaignList(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching campaigns:', err)
      }
    }
    fetchCampaigns()
  }, [activeTab, dateRange, singleSource, currentClinicId])
  
  // Fetch drilldown data when a campaign is selected
  useEffect(() => {
    async function fetchDrilldown() {
      if (!selectedCampaign || !dateRange?.from || !dateRange?.to) {
        setDrilldownData(null)
        return
      }
      
      try {
        setDrilldownLoading(true)
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')
        
        const response = await trafficAPI.getCampaignDrilldown({
          start,
          end,
          source: selectedCampaign.source,
          sub_source: selectedCampaign.sub_source,
        })
        
        if (response.data?.success) {
          setDrilldownData(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching drilldown:', err)
      } finally {
        setDrilldownLoading(false)
      }
    }
    fetchDrilldown()
  }, [selectedCampaign, dateRange, currentClinicId])
  
  // Fetch page traffic data when pages tab is active
  useEffect(() => {
    async function fetchPages() {
      if (activeTab !== 'pages' || !dateRange?.from || !dateRange?.to) return

      try {
        setPageLoading(true)
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')

        const response = await trafficAPI.getPages({ start, end, ...scope })
        if (response.data?.success) {
          setPageData(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching pages:', err)
      } finally {
        setPageLoading(false)
      }
    }
    fetchPages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, currentClinicId, scopeKey])

  // Fetch quality data when quality tab is active
  useEffect(() => {
    async function fetchQuality() {
      if (activeTab !== 'quality' || !dateRange?.from || !dateRange?.to) return

      try {
        setQualityLoading(true)
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')

        const response = await trafficAPI.getQuality({ start, end, ...scope })
        if (response.data?.success) {
          setQualityData(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching quality:', err)
      } finally {
        setQualityLoading(false)
      }
    }
    fetchQuality()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, currentClinicId, scopeKey])

  // Fetch the depth-weighted quality score + its trend when the quality tab is active
  useEffect(() => {
    async function fetchScore() {
      if (activeTab !== 'quality' || !dateRange?.from || !dateRange?.to) return

      try {
        setScoreLoading(true)
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')

        const params = { start, end, groupBy: scoreGroupBy, ...scope }

        const [scoreRes, trendRes] = await Promise.all([
          trafficAPI.getQualityScore(params),
          trafficAPI.getQualityScoreTrend({ ...params, interval: scoreInterval, topN: 6 }),
        ])
        if (scoreRes.data?.success) setScoreData(scoreRes.data.data)
        if (trendRes.data?.success) setScoreTrend(trendRes.data.data)
      } catch (err) {
        console.error('[TRAFFIC] Error fetching quality score:', err)
        setScoreData(null)
        setScoreTrend(null)
      } finally {
        setScoreLoading(false)
      }
    }
    fetchScore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, currentClinicId, scopeKey, scoreGroupBy, scoreInterval])

  // Fetch duration trends when durations tab is active
  useEffect(() => {
    async function fetchDurations() {
      if (activeTab !== 'durations' || !dateRange?.from || !dateRange?.to) return

      try {
        setDurationLoading(true)
        const start = format(dateRange.from, 'yyyy-MM-dd')
        const end = format(dateRange.to, 'yyyy-MM-dd')

        const response = await trafficAPI.getFunnelDurations({ start, end })
        if (response.data?.success) {
          setDurationData(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching funnel durations:', err)
      } finally {
        setDurationLoading(false)
      }
    }
    fetchDurations()
  }, [activeTab, dateRange, currentClinicId])

  // A funnel slug belongs to one clinic; switching clinic falls back to its biggest.
  useEffect(() => { setScreenFunnel(null) }, [currentClinicId])

  // Fetch the screen-by-screen funnel when the Intake Steps tab is active
  useEffect(() => {
    async function fetchScreenSteps() {
      if (activeTab !== 'steps' || !dateRange?.from || !dateRange?.to) return
      try {
        setScreenStepsLoading(true)
        setScreenStepsError(null)
        const params = {
          start: format(dateRange.from, 'yyyy-MM-dd'),
          end: format(dateRange.to, 'yyyy-MM-dd'),
        }
        if (screenFunnel) params.funnel = screenFunnel
        if (singleSource) params.source = singleSource
        const response = await trafficAPI.getIntakeScreenSteps(params)
        if (response.data?.success) setScreenSteps(response.data.data)
      } catch (err) {
        console.error('[TRAFFIC] Error fetching intake screen steps:', err)
        setScreenSteps(null)
        setScreenStepsError(err.response?.data?.error?.message || 'Failed to load intake steps')
      } finally {
        setScreenStepsLoading(false)
      }
    }
    fetchScreenSteps()
  }, [activeTab, dateRange, currentClinicId, singleSource, screenFunnel])

  // Fetch alias config when settings tab is active
  useEffect(() => {
    async function fetchAliases() {
      if (activeTab !== 'settings') return
      
      try {
        const response = await trafficAPI.getAliases()
        if (response.data?.success) {
          setAliasData(response.data.data)
        }
      } catch (err) {
        console.error('[TRAFFIC] Error fetching aliases:', err)
      }
    }
    fetchAliases()
  }, [activeTab, currentClinicId])
  
  // Helper to get display name for a campaign
  const getCampaignDisplayName = (rawName) => {
    if (!aliasData?.aliases) return rawName
    const alias = aliasData.aliases[rawName]
    return alias?.name || rawName
  }
  
  // Save alias
  const handleSaveAlias = async (rawName) => {
    if (!newAliasName.trim()) return
    
    try {
      await trafficAPI.addAlias({
        raw_name: rawName,
        display_name: newAliasName.trim(),
        group: newAliasGroup.trim() || null,
      })
      
      // Refresh aliases
      const response = await trafficAPI.getAliases()
      if (response.data?.success) {
        setAliasData(response.data.data)
      }
      
      setEditingAlias(null)
      setNewAliasName('')
      setNewAliasGroup('')
    } catch (err) {
      console.error('[TRAFFIC] Error saving alias:', err)
    }
  }
  
  // Delete alias
  const handleDeleteAlias = async (rawName) => {
    try {
      await trafficAPI.removeAlias(rawName)
      
      // Refresh aliases
      const response = await trafficAPI.getAliases()
      if (response.data?.success) {
        setAliasData(response.data.data)
      }
    } catch (err) {
      console.error('[TRAFFIC] Error deleting alias:', err)
    }
  }
  
  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true)
    // Force re-fetch by updating state
    setData(null)
    setLoading(true)
  }
  
  // Get color for a source category
  // Rows the backend adds that are not traffic sources at all.
  //
  // `unattributed` holds the conversions whose visitor has no session in the
  // selected window — a buyer whose first touch was earlier, or one whose
  // payment fired server-side under an id that never viewed a page. Without an
  // entry here it falls through to the default CATEGORY, which is "Other", and
  // the table would show two rows both labelled Other, one of them a residual.
  const SYNTHETIC_SOURCES = {
    unattributed: { name: 'Unattributed' },
    unknown: { name: 'Unknown' },
  }

  // Every source colour comes from lib/channels, never from the `color` the
  // /traffic/sources API sends — that config drifted from the app palette once
  // already. A category the registry does not know (and the synthetic
  // `unknown`) takes a fallback by its position in the source list, so two
  // unknowns in one chart stay distinguishable.
  const getSourceColor = (categoryId, index) => {
    if (resolveChannel(categoryId)) return channelColor(categoryId)
    if (categoryId === 'unknown') return channelColor('unattributed')
    const pos = index ?? sources?.categories?.findIndex(c => c.id === categoryId) ?? -1
    return channelColor(categoryId, pos >= 0 ? pos : 0)
  }
  
  // Get name for a source category
  const getSourceName = (categoryId) => {
    if (SYNTHETIC_SOURCES[categoryId]) return SYNTHETIC_SOURCES[categoryId].name
    if (!sources) return categoryId
    const cat = sources.categories?.find(c => c.id === categoryId)
    return cat?.name || sources.default?.name || categoryId
  }
  const sourceNames = sourceFilter.map(getSourceName).join(', ')
  
  // Prepare pie chart data
  const pieChartData = useMemo(() => {
    if (!data?.totals) return []
    const total = data.totals.reduce((sum, t) => sum + t.pageviews, 0)
    return data.totals.map(t => ({
      name: getSourceName(t.source_category),
      value: t.pageviews,
      visitors: t.unique_visitors,
      color: getSourceColor(t.source_category),
      id: t.source_category,
      total,
    }))
  }, [data, sources])
  
  // Prepare area chart data (daily trends)
  const areaChartData = useMemo(() => {
    if (!data?.daily) return []
    
    // Group by date
    const byDate = {}
    data.daily.forEach(d => {
      const dateStr = d.date
      if (!byDate[dateStr]) {
        byDate[dateStr] = { date: dateStr }
      }
      byDate[dateStr][d.source_category] = (byDate[dateStr][d.source_category] || 0) + d.pageviews
    })
    
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date))
  }, [data])

  // Prepare daily spend chart data (from marketing API)
  const dailySpendChartData = useMemo(() => {
    if (!marketingDaily || marketingDaily.length === 0) return []
    return marketingDaily.map(d => ({
      date: d.date,
      spend: d.total_spend || 0,
    }))
  }, [marketingDaily])

  // Get unique categories for chart
  const categories = useMemo(() => {
    if (!sources?.categories) return []
    return sources.categories.map(c => c.id)
  }, [sources])
  
  // Filter funnel data by category
  const filteredFunnelData = useMemo(() => {
    if (!funnelData?.funnel) return null
    if (!sourceFilter.length) return funnelData

    return {
      ...funnelData,
      funnel: funnelData.funnel.filter(f => sourceFilter.includes(f.source))
    }
  }, [funnelData, sourceFilter])

  // A source with no sessions has no conversion rate. `unknown` is exactly that:
  // payments whose visitor id never viewed a page (Science.co 2026-09-08..21:
  // 36 of 385 payments, none seen on any page since January). Dividing by
  // max(sessions, 1) turned its 52 payments into a 5,200% bar.
  const cvrBySource = useMemo(() => {
    const rows = (filteredFunnelData?.funnel || []).map(f => ({
      name: getSourceName(f.source),
      sessions: f.sessions || f.visitors || 0,
      payments: f.payments,
      color: getSourceColor(f.source),
    }))
    return {
      rated: rows.filter(f => f.sessions > 0).map(f => ({ ...f, cvr: f.payments / f.sessions * 100 })),
      unrated: rows.filter(f => f.sessions === 0 && f.payments > 0),
    }
  }, [filteredFunnelData, sources])
  
  // Sorted daily funnel data with current day marking and split data for dashed lines
  const sortedDailyFunnel = useMemo(() => {
    if (!dailyFunnelData?.daily) return []
    
    const today = format(new Date(), 'yyyy-MM-dd')
    
    const sorted = [...dailyFunnelData.daily]
      .sort((a, b) => a.date.localeCompare(b.date))
    
    // Find today's index
    const todayIndex = sorted.findIndex(d => d.date === today)
    
    return sorted.map((d, index) => {
      const isToday = d.date === today
      const isYesterday = todayIndex > 0 && index === todayIndex - 1
      
      return {
        ...d,
        isToday,
        displayDate: format(parse(d.date, 'yyyy-MM-dd', new Date()), 'MMM d'),
        // For solid lines: show data for all days except today
        sessions_complete: isToday ? null : d.sessions,
        visitors_complete: isToday ? null : d.visitors,
        emails_complete: isToday ? null : d.emails,
        payments_complete: isToday ? null : d.payments,
        // For dashed lines: show data only for yesterday (bridge) and today
        sessions_incomplete: (isYesterday || isToday) ? d.sessions : null,
        visitors_incomplete: (isYesterday || isToday) ? d.visitors : null,
        emails_incomplete: (isYesterday || isToday) ? d.emails : null,
        payments_incomplete: (isYesterday || isToday) ? d.payments : null,
      }
    })
  }, [dailyFunnelData])
  
  // Build hierarchical page tree from flat page data (with hostname at top level)
  //
  // A folder row does NOT sum its children for the people-counting metrics.
  // Payments, emails and visitors are attributed to every page a converting
  // visitor touched, so one purchase is recorded again on each step of the
  // funnel it walked through. Measured on scienceco, 2026-08-01 → 09-02: the
  // 15 payments of the /glp1-dosage funnel appear on all nine of its pages,
  // and summing the 210 page rows reports 328 payments for a clinic that took
  // about 21 charges in the window. Emails summed to 961 against a real 41.
  //
  // So those three roll up as MAX(own, children) — the deepest single truth
  // under this branch — while `pageviews` still SUMs, because a view of
  // /goal and a view of /bmi are two genuinely different events.
  //
  // MAX is a lower bound where SUM was a 15x upper bound: two sibling funnels
  // with different audiences read as the larger of the two rather than their
  // total. Where the backend supplies a properly deduped hostname rollup that
  // still wins, because it is exact rather than bounded.
  const pageTree = useMemo(() => {
    if (!pageData?.pages) return null

    // Metrics counting PEOPLE, which repeat across every page of a funnel.
    const PEOPLE_METRICS = ['visitors', 'emails', 'payments']

    const emptyNode = (props) => ({
      children: {},
      pageviews: 0,
      visitors: 0,
      emails: 0,
      payments: 0,
      ...props,
    })

    const root = emptyNode({ name: 'All Sites', path: '/', isFolder: true })

    // Each row lands on ITS OWN node only. Ancestors are computed afterwards,
    // which is what makes a non-sum rollup possible at all.
    pageData.pages.forEach(page => {
      const hostname = page.hostname || 'unknown'
      const path = page.page_path || '/'
      const parts = path.split('/').filter(Boolean)

      if (!root.children[hostname]) {
        root.children[hostname] = emptyNode({
          name: hostname, path: `/${hostname}`, fullPath: hostname, isHostname: true,
        })
      }
      const hostnameNode = root.children[hostname]

      let current = hostnameNode
      let currentPath = `/${hostname}`

      parts.forEach((part, index) => {
        currentPath += '/' + part
        if (!current.children[part]) {
          current.children[part] = emptyNode({
            name: part,
            path: currentPath,
            fullPath: `${hostname}${path.substring(0, path.indexOf(part) + part.length)}`,
            isFolder: index < parts.length - 1,
          })
        }
        current = current.children[part]
      })

      // The hostname root path "/" is its own child node, not the hostname.
      if (parts.length === 0) {
        if (!hostnameNode.children['/']) {
          hostnameNode.children['/'] = emptyNode({
            name: '/', path: `/${hostname}/`, fullPath: `${hostname}/`, isPage: true,
          })
        }
        current = hostnameNode.children['/']
      }

      current.isPage = true
      current.pageviews += page.pageviews || 0
      for (const m of PEOPLE_METRICS) current[m] += page[m] || 0
      current.cvr = page.cvr
    })

    // Roll up: pageviews sum, people-metrics take the deepest single value.
    const rollUp = (node) => {
      const children = Object.values(node.children)
      if (children.length === 0) return node
      children.forEach(rollUp)
      node.pageviews += children.reduce((sum, c) => sum + c.pageviews, 0)
      for (const m of PEOPLE_METRICS) {
        node[m] = Math.max(node[m], ...children.map(c => c[m]))
      }
      return node
    }
    rollUp(root)

    // A backend rollup is a real DISTINCT count per hostname, so it beats the
    // bound wherever it exists. The root then takes the max of those.
    const totals = pageData.hostnameTotals || {}
    if (Object.keys(totals).length > 0) {
      Object.entries(root.children).forEach(([hostname, node]) => {
        if (!totals[hostname]) return
        for (const m of PEOPLE_METRICS) node[m] = totals[hostname][m]
      })
      const hostnames = Object.values(root.children)
      for (const m of PEOPLE_METRICS) {
        root[m] = Math.max(0, ...hostnames.map(c => c[m]))
      }
    }

    // Convert children objects to sorted arrays
    const convertToArray = (node) => {
      const children = Object.values(node.children)
        .map(child => convertToArray(child))

      return {
        ...node,
        children,
        hasChildren: children.length > 0,
        cvr: node.visitors > 0 ? (node.payments / node.visitors * 100) : 0,
      }
    }

    return sortTrafficTree([convertToArray(root)], pageSort)[0]
  }, [pageData, pageSort])
  
  // Build hierarchical source tree from sub-source data
  // Structure: Source → Campaign (utm_campaign) → Content (utm_content)
  const sourceTree = useMemo(() => {
    const tree = buildSourceTree(subSourceData?.subSources, getSourceName)
    return tree && sortTrafficTree(tree, sourceSort)
  }, [subSourceData, sources, sourceSort])
  
  // Toggle expanded state for a path
  const togglePath = (path) => {
    setExpandedPaths(prev => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }
  
  // Toggle expanded state for a source
  const toggleSource = (sourceId) => {
    setExpandedSources(prev => {
      const next = new Set(prev)
      if (next.has(sourceId)) {
        next.delete(sourceId)
      } else {
        next.add(sourceId)
      }
      return next
    })
  }
  
  // Format number for AI output (no abbreviation)
  const formatNumberFull = (value) => value?.toLocaleString() || '0'
  
  // Format data for AI assistant based on current tab
  const getFormattedDataForAI = () => {
    const brand = data?.brand || currentClinicId || 'Unknown'
    const period = dateRange?.from && dateRange?.to 
      ? `${format(dateRange.from, 'MMM d, yyyy')} to ${format(dateRange.to, 'MMM d, yyyy')}`
      : 'Unknown period'
    
    let text = `# Traffic Analytics Report\n`
    text += `**Brand:** ${brand}\n`
    text += `**Period:** ${period}\n\n`
    
    if (activeTab === 'traffic') {
      // Traffic Overview
      text += `## Traffic Overview\n\n`
      text += `| Metric | Value |\n|--------|-------|\n`
      text += `| Total Pageviews | ${formatNumberFull(data?.summary?.total_pageviews)} |\n`
      text += `| Unique Visitors | ${formatNumberFull(data?.summary?.total_visitors)} |\n`
      
      if (todayData) {
        text += `\n### Today (Live)\n`
        text += `- Pageviews: ${formatNumberFull(todayData.pageviews)}\n`
        text += `- Visitors: ${formatNumberFull(todayData.visitors)}\n`
        text += `- Emails: ${formatNumberFull(todayData.emails)}\n`
        text += `- Payments: ${formatNumberFull(todayData.payments)}\n`
      }
      
      if (data?.totals?.length > 0) {
        text += `\n### Traffic by Source\n\n`
        text += `| Source | Pageviews | Visitors | % of Total |\n|--------|-----------|----------|------------|\n`
        const totalPV = data.totals.reduce((sum, t) => sum + t.pageviews, 0)
        data.totals.forEach(t => {
          const pct = ((t.pageviews / totalPV) * 100).toFixed(1)
          text += `| ${getSourceName(t.source_category)} | ${formatNumberFull(t.pageviews)} | ${formatNumberFull(t.unique_visitors)} | ${pct}% |\n`
        })
      }
    }
    
    if (activeTab === 'funnel') {
      // Funnel Data
      text += `## Conversion Funnel\n\n`
      
      if (funnelData?.totals) {
        const t = funnelData.totals
        const emailRate = t.visitors > 0 ? ((t.emails / t.visitors) * 100).toFixed(2) : 0
        const paymentRate = t.visitors > 0 ? ((t.payments / t.visitors) * 100).toFixed(2) : 0
        
        text += `### Overall Metrics\n`
        text += `| Stage | Count | Conversion Rate |\n|-------|-------|----------------|\n`
        text += `| Visitors | ${formatNumberFull(t.visitors)} | 100% |\n`
        text += `| Emails Captured | ${formatNumberFull(t.emails)} | ${emailRate}% |\n`
        text += `| Payments | ${formatNumberFull(t.payments)} | ${paymentRate}% |\n`
      }
      
      if (subSourceData?.breakdown?.length > 0) {
        text += `\n### By Campaign/Source (Top 15)\n\n`
        text += `| Source | Campaign | Visitors | Emails | Payments | CVR |\n|--------|----------|----------|--------|----------|-----|\n`
        subSourceData.breakdown.slice(0, 15).forEach(row => {
          const cvr = row.visitors > 0 ? ((row.payments / row.visitors) * 100).toFixed(2) : 0
          text += `| ${getSourceName(row.source)} | ${decodeUTM(row.sub_source).slice(0, 30)} | ${formatNumberFull(row.visitors)} | ${formatNumberFull(row.emails)} | ${formatNumberFull(row.payments)} | ${cvr}% |\n`
        })
      }
    }
    
    if (activeTab === 'drilldown' && campaignList?.campaigns?.length > 0) {
      text += `## Campaign Performance\n\n`
      text += `| Source | Campaign | Visitors | Emails | Payments | CVR |\n|--------|----------|----------|--------|----------|-----|\n`
      campaignList.campaigns.slice(0, 20).forEach(c => {
        text += `| ${getSourceName(c.source)} | ${String(c.sub_source || '').slice(0, 40)} | ${formatNumberFull(c.visitors)} | ${formatNumberFull(c.emails)} | ${formatNumberFull(c.payments)} | ${c.cvr?.toFixed(2) || 0}% |\n`
      })
    }
    
    if (activeTab === 'pages' && pageData?.pages?.length > 0) {
      text += `## Traffic by Page (Top 30)\n\n`
      text += `| Hostname | Path | Pageviews | Visitors | Emails | Payments | CVR |\n|----------|------|-----------|----------|--------|----------|-----|\n`
      pageData.pages.slice(0, 30).forEach(p => {
        text += `| ${p.hostname || 'unknown'} | ${p.page_path} | ${formatNumberFull(p.pageviews)} | ${formatNumberFull(p.visitors)} | ${formatNumberFull(p.emails)} | ${formatNumberFull(p.payments)} | ${p.cvr?.toFixed(2) || 0}% |\n`
      })
    }
    
    if (activeTab === 'steps' && screenSteps?.steps?.length > 0) {
      text += `## Intake Steps — /${screenSteps.funnel} (ordered, ${screenSteps.window_days}-day window)\n\n`
      text += `| # | Step | People | % of first | Step conv. | Drop-off | Median time |\n|---|------|--------|------------|------------|----------|-------------|\n`
      screenSteps.steps.forEach(s => {
        text += `| ${s.step_id} | ${screenLabel(s)} | ${formatNumberFull(s.people)} | ${formatPct(s.pct_of_first)} | ${formatPct(s.pct_of_previous)} | ${formatPct(s.dropoff_from_previous)} | ${formatDuration(s.p50_secs_from_previous)} |\n`
      })
    }

    text += `\n---\n*Generated from Traffic Analytics on ${format(new Date(), 'MMM d, yyyy HH:mm')}*`
    
    return text
  }
  
  // Error state
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Traffic Analytics</h1>
        </div>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Traffic Data</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    )
  }
  
  const totalPageviews = data?.summary?.total_pageviews || 0
  const totalVisitors = data?.summary?.total_visitors || 0
  const topCategory = data?.summary?.top_category || 'none'
  
  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">Traffic Analytics</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Segment data from BigQuery
            {data?.isMock && ' (Mock data - BigQuery not configured)'}
          </p>
        </div>
      </div>
      
      <PaymentFreshnessStrip dateRange={dateRange} />

      <DataFreshnessWarning
        daily={data?.daily}
        dateRange={dateRange}
        loading={loading}
      />

      {/* Today's Real-time Stats */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Today (Live)</span>
              {todayData?.brand && (
                <Badge variant="outline" className="text-xs">
                  {todayData.brand}
                </Badge>
              )}
            </div>
            {todayLoading ? (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            ) : (
              <span className="text-xs text-muted-foreground">
                Updates every minute • {currentClinicId}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xl sm:text-2xl font-bold">
                {todayLoading ? '...' : formatNumber(todayData?.total_visitors || 0)}
              </div>
              <div className="text-xs text-muted-foreground">Visitors</div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold">
                {todayLoading ? '...' : formatNumber(todayData?.total_pageviews || 0)}
              </div>
              <div className="text-xs text-muted-foreground">Pageviews</div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold text-purple-500">
                {todayLoading ? '...' : formatNumber(todayData?.total_emails || 0)}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" /> Emails
              </div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold text-green-500">
                {todayLoading ? '...' : formatNumber(todayData?.total_payments || 0)}
              </div>
              <div className="text-xs text-muted-foreground">Payments</div>
            </div>
          </div>
          {todayData?.by_source?.length > 0 && !todayLoading && (
            <div className="mt-3 pt-3 border-t border-primary/10">
              <div className="flex flex-wrap gap-2">
                {todayData.by_source.slice(0, 5).map(s => (
                  <Badge key={s.source} variant="secondary" className="text-xs">
                    <div 
                      className="w-2 h-2 rounded-full mr-1.5" 
                      style={{ backgroundColor: getSourceColor(s.source) }}
                    />
                    {getSourceName(s.source)}: {formatNumber(s.visitors)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Controls: dates, then Source → Campaign → Content, then actions.
          Everything below narrows to the filter; Today (Live) above does not. */}
      <ReportToolbar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onRefresh={handleRefresh}
        refreshing={isRefreshing || loading}
        actions={<CopyForAIButton getText={getFormattedDataForAI} />}
      >
        <SearchMultiSelect
          options={(sources?.categories || []).map(cat => ({ value: cat.id, label: cat.name, color: getSourceColor(cat.id) }))}
          selected={sourceFilter} onChange={setSourceFilter}
          allLabel="All sources" noun="sources" searchPlaceholder="Search sources…" className="w-44"
        />

        {sourceFilter.length > 0 && scopeOpts.campaigns.length > 0 && (
          <SearchMultiSelect
            options={scopeOpts.campaigns.map(c => ({ value: c.label, label: c.label, hint: formatNumber(c.visitors) }))}
            selected={campaignFilter} onChange={setCampaignFilter}
            allLabel="All campaigns" noun="campaigns" searchPlaceholder="Search campaigns…" className="w-56"
          />
        )}

        {campaignFilter.length > 0 && contentOpts.length > 0 && (
          <SearchMultiSelect
            options={contentOpts.map(c => ({ value: c.label, label: c.label, hint: formatNumber(c.visitors) }))}
            selected={contentFilter} onChange={setContentFilter}
            allLabel="All content" noun="content" searchPlaceholder="Search content…" className="w-56"
          />
        )}

        {sourceFilter.length > 0 && (
          <Button variant="ghost" size="sm" className="h-9" onClick={() => setSourceFilter([])}>
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        )}

        <button
          onClick={() => setShowSourceInfo(showSourceInfo ? null : 'info')}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          title="What's in each bucket?"
        >
          <AlertCircle className="h-4 w-4" />
        </button>
      </ReportToolbar>
      
      {/* Mock data warning */}
      {data?.isMock && (
        <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-foreground">
                Showing mock data. Configure <code className="px-1 bg-amber-100 dark:bg-amber-900 rounded">BIGQUERY_PROJECT_ID</code> and service account key to see real traffic data.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Pageviews"
          value={formatNumber(totalPageviews)}
          subtitle={`${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d, yyyy')} · vs prior period`}
          icon={Eye}
          loading={loading}
          color="#3B82F6"
          delta={{ current: totalPageviews, prior: data?.summary?.prior_period?.total_pageviews }}
        />
        <SummaryCard
          title="Unique Visitors"
          value={formatNumber(totalVisitors)}
          subtitle={`~${totalPageviews > 0 ? (totalPageviews / totalVisitors).toFixed(1) : 0} pages/visitor`}
          icon={Users}
          loading={loading}
          color="#10B981"
          delta={{ current: totalVisitors, prior: data?.summary?.prior_period?.total_visitors }}
        />
        <SummaryCard
          title="Session → Payment"
          value={`${(
            (filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.payments, 0) || 0) /
            Math.max(filteredFunnelData?.funnel?.reduce((sum, f) => sum + (f.sessions || f.visitors), 0) || 1, 1) * 100
          ).toFixed(2)}%`}
          subtitle={`${formatNumber(filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.payments, 0) || 0)} payments${sourceFilter.length ? ` (${sourceNames})` : ''}`}
          icon={TrendingUp}
          loading={funnelLoading}
          color="#8B5CF6"
        />
        <SummaryCard
          title="Top Source"
          value={getSourceName(topCategory)}
          subtitle={`${formatNumber(data?.summary?.top_category_pageviews || 0)} pageviews`}
          icon={Globe}
          loading={loading}
          color={getSourceColor(topCategory)}
        />
      </div>
      
      {/* CVR by Source Chart - always visible */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">
            Conversion Rate by Source
            {sourceFilter.length > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                (showing {sourceNames})
              </span>
            )}
          </CardTitle>
          <CardDescription>Session → Payment % by traffic source (higher is better)</CardDescription>
        </CardHeader>
        <CardContent>
          {funnelLoading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : cvrBySource.rated.length > 0 ? (
            <>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart 
                data={cvrBySource.rated}
                layout="vertical"
                margin={{ left: 80, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis 
                  type="number" 
                  domain={[0, 'auto']}
                  tickFormatter={(v) => `${v.toFixed(1)}%`}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fontSize: 11 }}
                  width={75}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload
                      return (
                        <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm">
                          <p className="font-medium">{d.name}</p>
                          <p className="text-lg font-bold" style={{ color: d.color }}>{d.cvr.toFixed(2)}% CVR</p>
                          <p className="text-xs text-muted-foreground">{formatNumber(d.sessions)} sessions → {formatNumber(d.payments)} payments</p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Bar 
                  dataKey="cvr" 
                  radius={[0, 4, 4, 0]}
                  fill="#8B5CF6"
                >
                  {cvrBySource.rated.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {cvrBySource.unrated.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Not charted: {cvrBySource.unrated.map(f => `${formatNumber(f.payments)} ${f.name}`).join(', ')} payment{cvrBySource.unrated.reduce((n, f) => n + f.payments, 0) === 1 ? '' : 's'} with
                no session in this window, so there is no rate to show. Usually the payment was recorded under a visitor id that never viewed a page.
              </p>
            )}
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              No funnel data available
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Tab Switcher */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between border-b border-border pb-2">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveTab('traffic')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'traffic' 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Eye className="h-4 w-4 inline-block mr-2" />
            Traffic
          </button>
          <button
            onClick={() => setActiveTab('funnel')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'funnel' 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <TrendingUp className="h-4 w-4 inline-block mr-2" />
            Conversion Funnel
          </button>
          <button
            onClick={() => { setActiveTab('drilldown'); setSelectedCampaign(null) }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'drilldown' 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Search className="h-4 w-4 inline-block mr-2" />
            Campaign Drill-down
          </button>
          <button
            onClick={() => setActiveTab('pages')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'pages'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <FileText className="h-4 w-4 inline-block mr-2" />
            Pages
          </button>
          <button
            onClick={() => setActiveTab('durations')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'durations'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Clock className="h-4 w-4 inline-block mr-2" />
            Duration Trends
          </button>
          <button
            onClick={() => setActiveTab('steps')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'steps'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <ListOrdered className="h-4 w-4 inline-block mr-2" />
            Intake Steps
          </button>
          <button
            onClick={() => setActiveTab('quality')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'quality'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Zap className="h-4 w-4 inline-block mr-2" />
            Quality
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'settings' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Settings className="h-4 w-4 inline-block mr-2" />
              Settings
            </button>
          )}
        </div>
        
      </div>
      
      {/* Source Info Panel */}
      {showSourceInfo && (
        <Card className="bg-muted/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Traffic Source Definitions
            </CardTitle>
            <CardDescription className="text-xs">How visitors are classified into each bucket</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sources?.categories?.map(cat => (
                <div key={cat.id} className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSourceColor(cat.id) }} />
                    <span className="font-medium text-sm">{cat.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{cat.description || 'No description available'}</p>
                </div>
              ))}
              {sources?.default && (
                <div className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSourceColor(sources.default.id) }} />
                    <span className="font-medium text-sm">{sources.default.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{sources.default.description || 'No description available'}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {activeTab === 'traffic' && (
      <>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Source Distribution Donut */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Traffic by Source</CardTitle>
            <CardDescription>Distribution of pageviews by channel</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : pieChartData.length > 0 ? (
              <div className="relative">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} wrapperStyle={{ zIndex: 100 }} />
                    <Legend 
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center label */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginBottom: '36px' }}>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                    <div className="text-xl font-bold text-foreground">{formatNumber(totalPageviews)}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Daily Trends Area Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Daily Traffic Trends</CardTitle>
            <CardDescription>Pageviews by source over time</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : areaChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={areaChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    interval={Math.floor(areaChartData.length / 6)}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatNumber(v)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {sources?.categories?.map((cat, idx) => (
                    <Area
                      key={cat.id}
                      type="monotone"
                      dataKey={cat.id}
                      name={cat.name}
                      stackId="1"
                      stroke={getSourceColor(cat.id, idx)}
                      fill={getSourceColor(cat.id, idx)}
                      fillOpacity={0.6}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Spend Chart — side-by-side comparison with traffic */}
      {dailySpendChartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Daily Ad Spend</CardTitle>
            <CardDescription>Total marketing spend over time — compare with traffic trends above for efficiency</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={dailySpendChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(dailySpendChartData.length / 6)}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip formatter={(v) => [`$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 'Spend']} />
                <Area
                  type="monotone"
                  dataKey="spend"
                  stroke="#F59E0B"
                  fill="#F59E0B"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      
      {/* Source → Campaign → Content Tree */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Source Breakdown</CardTitle>
          <CardDescription>
            Source → Campaign → Content hierarchy (click to expand)
            <button 
              className="ml-4 text-xs text-primary hover:underline"
              onClick={() => setExpandedSources(new Set())}
            >
              Collapse All
            </button>
            <button 
              className="ml-2 text-xs text-primary hover:underline"
              onClick={() => {
                if (sourceTree) {
                  // Expand all sources and campaigns
                  const allIds = new Set()
                  sourceTree.forEach(s => {
                    allIds.add(s.id)
                    s.children.forEach(c => allIds.add(c.id))
                  })
                  setExpandedSources(allIds)
                }
              }}
            >
              Expand All
            </button>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading || funnelLoading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 bg-muted rounded"></div>
              ))}
            </div>
          ) : sourceTree?.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Header */}
              <div className="grid grid-cols-[1fr_70px_70px_70px_70px_60px] gap-2 border-b pb-2 text-xs font-medium text-muted-foreground">
                <TrafficTreeSortHeaders nameLabel="Source / Campaign / Content" sort={sourceSort} onSort={setSourceSort} />
              </div>
              
              {/* Tree Rows */}
              <div className="mt-2">
                {sourceTree.map((source) => (
                  <SourceTreeNode
                    key={source.id}
                    node={source}
                    depth={0}
                    expandedSources={expandedSources}
                    toggleSource={toggleSource}
                    formatNumber={formatNumber}
                    getSourceColor={getSourceColor}
                  />
                ))}
              </div>
              
              {/* Total row */}
              {sourceTree.length > 0 && (
                <div className="grid grid-cols-[1fr_70px_70px_70px_70px_60px] gap-2 border-t mt-2 pt-2 text-xs font-bold bg-muted/30 -mx-6 px-6 py-2">
                  <div className="flex items-center gap-2">
                    <span>📊</span>
                    <span>Total ({sourceTree.length} sources, {sourceTree.reduce((sum, s) => sum + s.children.length, 0)} campaigns)</span>
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + (s.pageviews || 0), 0))}
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + s.visitors, 0))}
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + s.emails, 0))}
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + s.payments, 0))}
                  </div>
                  <div className="text-right">
                    {(() => {
                      const totalV = sourceTree.reduce((sum, s) => sum + s.visitors, 0)
                      const totalP = sourceTree.reduce((sum, s) => sum + s.payments, 0)
                      const cvr = totalV > 0 ? (totalP / totalV * 100) : 0
                      return (
                        <span style={{ color: cvr >= 10 ? '#10B981' : cvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                          {cvr.toFixed(1)}%
                        </span>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              {data?.totals?.length > 0 
                ? 'Loading campaign breakdown...' 
                : 'No traffic data available for the selected period'}
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
      
      {activeTab === 'funnel' && (
      <>
      {/* Funnel Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard
          title={sourceFilter.length ? `${sourceNames} Sessions` : "Total Sessions"}
          value={formatNumber(filteredFunnelData?.funnel?.reduce((sum, f) => sum + (f.sessions || 0), 0) || 0)}
          subtitle="All page visits"
          icon={Eye}
          loading={funnelLoading}
          color="#F59E0B"
        />
        <SummaryCard
          title="Intake Visitors"
          value={formatNumber(filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.visitors, 0) || 0)}
          subtitle={`${(
            (filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.visitors, 0) || 0) /
            Math.max(filteredFunnelData?.funnel?.reduce((sum, f) => sum + (f.sessions || 0), 0) || 1, 1) * 100
          ).toFixed(1)}% of sessions`}
          icon={Users}
          loading={funnelLoading}
          color="#3B82F6"
        />
        <SummaryCard
          title="Emails Collected"
          value={formatNumber(filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.emails, 0) || 0)}
          subtitle="email_entered events"
          icon={Mail}
          loading={funnelLoading}
          color="#8B5CF6"
        />
        <SummaryCard
          title="Payments"
          value={formatNumber(filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.payments, 0) || 0)}
          subtitle="payment_succeeded events"
          icon={TrendingUp}
          loading={funnelLoading}
          color="#10B981"
        />
        <SummaryCard
          title="Overall CVR"
          value={`${(
            (filteredFunnelData?.funnel?.reduce((sum, f) => sum + f.payments, 0) || 0) /
            Math.max(filteredFunnelData?.funnel?.reduce((sum, f) => sum + (f.sessions || 0), 0) || 1, 1) * 100
          ).toFixed(1)}%`}
          subtitle="Session → Payment"
          icon={TrendingUp}
          loading={funnelLoading}
          color="#EF4444"
        />
      </div>
      
      {/* Funnel by Source */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">
            Conversion Funnel by Source
            {sourceFilter.length > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                (showing {sourceNames} only)
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Session Start → Intake → Emails → Payments by traffic source.
            A session starts wherever the visitor arrived — for some sources that is the
            intake page itself, which is why those reach intake at 100%.
            Emails count browser IDs that entered an email during this period, including returning leads;
            Daily Report counts newly created leads. Payments without page attribution appear as Unknown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnelLoading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-muted rounded"></div>
              ))}
            </div>
          ) : filteredFunnelData?.funnel?.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {funnelBase
                  ? <>Every step is shown as a % of <strong className="text-foreground">{FUNNEL_STEPS.find(step => step.key === funnelBase).label}</strong>. Click it again to go back to step-over-step.</>
                  : 'Each step is shown as a % of the step before it. Click Session Start, Intake or Emails to measure every step against that one.'}
              </p>
              {filteredFunnelData.funnel.map((row) => {
                const cvrBase = funnelBase || 'sessions'
                const cvrBaseLabel = FUNNEL_STEPS.find(step => step.key === cvrBase).label
                return (
                <div key={row.source} className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getSourceColor(row.source) }}
                      />
                      <span className="font-medium">{getSourceName(row.source)}</span>
                    </div>
                    <Badge variant="outline" className="text-xs" title={`Payments ÷ ${cvrBaseLabel}`}>
                      {row[cvrBase] > 0 ? `${((row.payments / row[cvrBase]) * 100).toFixed(1)}%` : '—'} CVR{funnelBase ? ` of ${cvrBaseLabel.toLowerCase()}` : ''}
                    </Badge>
                  </div>
                  
                  {/* Funnel bars.
                      Session Start is where the visitor actually arrived, which
                      for some sources IS the intake host — Science.co's Reddit
                      ads point straight at intake.science.co/glp1-reddit. A
                      separate "landing page" step scored Reddit at 34 of 801
                      and read as a 96% collapse; it is not a drop, just a
                      different front door, and those visitors reach intake at
                      100%. So it is one step, and the default bar base. */}
                  <div className="flex items-center gap-2">
                    {FUNNEL_STEPS.map((step, index) => {
                      const view = funnelStepView(row, step.key, funnelBase)
                      const selected = funnelBase === step.key
                      const content = <>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                          <span className={selected ? 'font-semibold text-foreground underline underline-offset-2' : ''}>{step.label}</span>
                          <span className="font-medium text-foreground" title={view.title}>
                            {formatNumber(row[step.key] || 0)}{view.pct !== undefined && ` (${view.pct == null ? '—' : `${view.pct.toFixed(1)}%`})`}
                          </span>
                        </div>
                        <div className={`h-6 bg-muted rounded-full overflow-hidden ${selected ? 'ring-2 ring-offset-1 ring-primary' : ''}`}>
                          <div 
                            className="h-full rounded-full"
                            style={{ width: `${view.width}%`, backgroundColor: getSourceColor(row.source), opacity: view.muted ? 0.25 : step.opacity }}
                          />
                        </div>
                      </>
                      return <div key={step.key} className="contents">
                        {index > 0 && <div className="text-muted-foreground px-1">→</div>}
                        {step.selectable
                          ? <button type="button" className="flex-1 text-left rounded-md hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-pressed={selected}
                              onClick={() => setFunnelBase(current => current === step.key ? null : step.key)}>{content}</button>
                          : <div className="flex-1">{content}</div>}
                      </div>
                    })}
                  </div>
                </div>
              )})}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No funnel data available for the selected period
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Daily Funnel Trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Daily Funnel Trend</CardTitle>
          <CardDescription>
            Sessions → Intake → Emails → Payments over time
            {sortedDailyFunnel.some(d => d.isToday) && (
              <span className="ml-2 text-xs">(dashed line = today, incomplete)</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnelLoading ? (
            <div className="h-[300px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedDailyFunnel.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={sortedDailyFunnel}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="displayDate" 
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(sortedDailyFunnel.length / 7)}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const dataPoint = payload[0]?.payload
                      // Show original values, not split values
                      return (
                        <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm z-50">
                          <p className="font-medium text-popover-foreground mb-2">
                            {label}
                            {dataPoint?.isToday && <span className="text-muted-foreground ml-1">(incomplete)</span>}
                          </p>
                          <p style={{ color: '#F59E0B' }} className="flex justify-between gap-4">
                            <span>Sessions:</span>
                            <span className="font-medium">{formatNumber(dataPoint?.sessions)}</span>
                          </p>
                          <p style={{ color: '#3B82F6' }} className="flex justify-between gap-4">
                            <span>Intake:</span>
                            <span className="font-medium">{formatNumber(dataPoint?.visitors)}</span>
                          </p>
                          <p style={{ color: '#8B5CF6' }} className="flex justify-between gap-4">
                            <span>Emails:</span>
                            <span className="font-medium">{formatNumber(dataPoint?.emails)}</span>
                          </p>
                          <p style={{ color: '#10B981' }} className="flex justify-between gap-4">
                            <span>Payments:</span>
                            <span className="font-medium">{formatNumber(dataPoint?.payments)}</span>
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Legend 
                  formatter={(value) => {
                    // Hide incomplete series from legend
                    if (value.includes('incomplete')) return null
                    return value.replace('_complete', '')
                  }}
                  payload={[
                    { value: 'Sessions', type: 'line', color: '#F59E0B' },
                    { value: 'Intake', type: 'line', color: '#3B82F6' },
                    { value: 'Emails', type: 'line', color: '#8B5CF6' },
                    { value: 'Payments', type: 'line', color: '#10B981' },
                  ]}
                />
                {/* Solid lines for complete data */}
                <Line 
                  type="monotone" 
                  dataKey="sessions_complete" 
                  name="Sessions" 
                  stroke="#F59E0B" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="visitors_complete" 
                  name="Intake" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="emails_complete" 
                  name="Emails" 
                  stroke="#8B5CF6" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="payments_complete" 
                  name="Payments" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
                {/* Dashed lines for incomplete (today's) data - connects from yesterday to today */}
                {sortedDailyFunnel.some(d => d.isToday) && (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="sessions_incomplete" 
                      name="Sessions (today)" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3, fill: '#F59E0B' }}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                      legendType="none"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="visitors_incomplete" 
                      name="Intake (today)" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3, fill: '#3B82F6' }}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                      legendType="none"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="emails_incomplete" 
                      name="Emails (today)" 
                      stroke="#8B5CF6" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3, fill: '#8B5CF6' }}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                      legendType="none"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="payments_incomplete" 
                      name="Payments (today)" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3, fill: '#10B981' }}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                      legendType="none"
                    />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No daily funnel data available
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Sub-Source Breakdown - Tree View */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">
            Campaign / Sub-Source Breakdown
            {sourceFilter.length > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                (filtered to {scopeLabel({ sources: sourceFilter, campaigns: campaignFilter, contents: contentFilter }, getSourceName)})
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Performance by source and campaign (click to expand)
            <button 
              className="ml-4 text-xs text-primary hover:underline"
              onClick={() => setExpandedSources(new Set())}
            >
              Collapse All
            </button>
            <button 
              className="ml-2 text-xs text-primary hover:underline"
              onClick={() => {
                if (sourceTree) {
                  setExpandedSources(new Set(sourceTree.map(s => s.id)))
                }
              }}
            >
              Expand All
            </button>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnelLoading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-muted rounded"></div>
              ))}
            </div>
          ) : sourceTree?.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Header */}
              <div className="grid grid-cols-[1fr_80px_80px_80px_80px_80px] gap-2 border-b pb-2 text-sm font-medium">
                <TrafficTreeSortHeaders nameLabel="Source / Campaign" sort={sourceSort} onSort={setSourceSort} />
              </div>
              
              {/* Tree Rows */}
              <div className="mt-2">
                {(!sourceFilter.length 
                  ? sourceTree 
                  : sourceTree.filter(s => sourceFilter.includes(s.sourceId))
                ).map((source) => (
                  <SourceTreeNode
                    key={source.id}
                    node={source}
                    depth={0}
                    expandedSources={expandedSources}
                    toggleSource={toggleSource}
                    formatNumber={formatNumber}
                    getSourceColor={getSourceColor}
                  />
                ))}
              </div>
              
              {/* Total row */}
              {!sourceFilter.length && sourceTree.length > 0 && (
                <div className="grid grid-cols-[1fr_80px_80px_80px_80px_80px] gap-2 border-t mt-2 pt-2 text-sm font-bold bg-muted/30 -mx-6 px-6 py-2">
                  <div className="flex items-center gap-2">
                    <span>📊</span>
                    <span>Total ({sourceTree.length} sources)</span>
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + (s.pageviews || 0), 0))}
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + s.visitors, 0))}
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + s.emails, 0))}
                  </div>
                  <div className="text-right">
                    {formatNumber(sourceTree.reduce((sum, s) => sum + s.payments, 0))}
                  </div>
                  <div className="text-right">
                    {(() => {
                      const totalV = sourceTree.reduce((sum, s) => sum + s.visitors, 0)
                      const totalP = sourceTree.reduce((sum, s) => sum + s.payments, 0)
                      const cvr = totalV > 0 ? (totalP / totalV * 100) : 0
                      return (
                        <span style={{ color: cvr >= 10 ? '#10B981' : cvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                          {cvr.toFixed(2)}%
                        </span>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No sub-source data available for the selected period
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
      
      {/* Drill-down Tab */}
      {activeTab === 'drilldown' && (
      <>
      {selectedCampaign ? (
        /* Campaign Detail View */
        <div className="space-y-4">
          {/* Back button */}
          <button
            onClick={() => setSelectedCampaign(null)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to campaign list
          </button>
          
          {/* Campaign Header */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: getSourceColor(selectedCampaign.source) }}
                />
                <div>
                  <CardTitle className="text-lg">
                    {selectedCampaign.display_name || selectedCampaign.sub_source}
                  </CardTitle>
                  <CardDescription>
                    {getSourceName(selectedCampaign.source)} • {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
          
          {drilldownLoading ? (
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ) : drilldownData ? (
            <>
            {/* Comparison Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* This Campaign */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-primary">This Campaign</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">{formatNumber(drilldownData.campaign.visitors)}</div>
                      <div className="text-xs text-muted-foreground">Visitors</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-500">{formatNumber(drilldownData.campaign.emails)}</div>
                      <div className="text-xs text-muted-foreground">Emails</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-500">{formatNumber(drilldownData.campaign.payments)}</div>
                      <div className="text-xs text-muted-foreground">Payments</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t text-center">
                    <span className="text-lg font-bold">
                      {((drilldownData.campaign.payments / Math.max(drilldownData.campaign.visitors, 1)) * 100).toFixed(2)}%
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">CVR</span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Rest of Source */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Rest of {getSourceName(selectedCampaign.source)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">{formatNumber(drilldownData.rest_of_source.visitors)}</div>
                      <div className="text-xs text-muted-foreground">Visitors</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-500">{formatNumber(drilldownData.rest_of_source.emails)}</div>
                      <div className="text-xs text-muted-foreground">Emails</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-500">{formatNumber(drilldownData.rest_of_source.payments)}</div>
                      <div className="text-xs text-muted-foreground">Payments</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t text-center">
                    <span className="text-lg font-bold text-muted-foreground">
                      {((drilldownData.rest_of_source.payments / Math.max(drilldownData.rest_of_source.visitors, 1)) * 100).toFixed(2)}%
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">CVR</span>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Performance Comparison */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Performance vs Rest of Source</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const campaignCVR = (drilldownData.campaign.payments / Math.max(drilldownData.campaign.visitors, 1)) * 100
                  const restCVR = (drilldownData.rest_of_source.payments / Math.max(drilldownData.rest_of_source.visitors, 1)) * 100
                  const diff = campaignCVR - restCVR
                  const isPositive = diff > 0
                  
                  return (
                    <div className="flex items-center gap-4">
                      <div className={`text-2xl font-bold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                        {isPositive ? '+' : ''}{diff.toFixed(2)}%
                      </div>
                      <div className="text-sm text-muted-foreground">
                        This campaign is performing {isPositive ? 'better' : 'worse'} than other {getSourceName(selectedCampaign.source)} traffic
                      </div>
                    </div>
                  )
                })()}
              </CardContent>
            </Card>
            
            {/* Daily Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Daily Performance</CardTitle>
                <CardDescription>Campaign performance over time</CardDescription>
              </CardHeader>
              <CardContent>
                {drilldownData.daily_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={drilldownData.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        interval={Math.floor(drilldownData.daily_trend.length / 7)}
                      />
                      <YAxis 
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="visitors" name="Visitors" fill="#3B82F6" fillOpacity={0.3} stroke="#3B82F6" />
                      <Area type="monotone" dataKey="emails" name="Emails" fill="#8B5CF6" fillOpacity={0.3} stroke="#8B5CF6" />
                      <Area type="monotone" dataKey="payments" name="Payments" fill="#10B981" fillOpacity={0.3} stroke="#10B981" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No daily trend data available
                  </div>
                )}
              </CardContent>
            </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Failed to load campaign data
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        /* Campaign List View */
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Select a Campaign to Analyze</CardTitle>
            <CardDescription>
              Select a normalized UTM campaign attributed by each user's first-touch source
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg bg-background"
                />
              </div>
            </div>
            
            {/* Campaign List */}
            {campaignList?.campaigns?.length > 0 ? (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {campaignList.campaigns
                  .filter(c => 
                    campaignSearch === '' || 
                    c.display_name?.toLowerCase().includes(campaignSearch.toLowerCase()) ||
                    c.sub_source?.toLowerCase().includes(campaignSearch.toLowerCase()) ||
                    c.source?.toLowerCase().includes(campaignSearch.toLowerCase())
                  )
                  .map((campaign) => (
                    <button
                      key={`${campaign.source}:${campaign.sub_source}`}
                      onClick={() => setSelectedCampaign({
                        source: campaign.source,
                        sub_source: campaign.sub_source,
                        display_name: campaign.display_name,
                      })}
                      className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div 
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: getSourceColor(campaign.source) }}
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate" title={campaign.sub_source}>
                              {(campaign.display_name || campaign.sub_source).slice(0, 40)}
                              {(campaign.display_name || campaign.sub_source).length > 40 ? '...' : ''}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {getSourceName(campaign.source)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 shrink-0 text-right">
                          <div>
                            <div className="text-sm font-medium">{formatNumber(campaign.visitors)}</div>
                            <div className="text-xs text-muted-foreground">visitors</div>
                          </div>
                          <div>
                            <div className="text-sm font-medium">{formatNumber(campaign.payments)}</div>
                            <div className="text-xs text-muted-foreground">payments</div>
                          </div>
                          <div>
                            <div 
                              className="text-sm font-bold"
                              style={{ 
                                color: campaign.cvr >= 10 ? '#10B981' : campaign.cvr >= 5 ? '#F59E0B' : '#6B7280'
                              }}
                            >
                              {campaign.cvr?.toFixed(1)}%
                            </div>
                            <div className="text-xs text-muted-foreground">CVR</div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                {loading ? (
                  <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                ) : (
                  'No campaigns found for the selected period'
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      </>
      )}
      
      {/* Pages Tab */}
      {activeTab === 'pages' && (
      <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Traffic by Page</CardTitle>
          <CardDescription>
            Page path breakdown with conversion metrics
            {/* Folder rows deliberately do not add up. One purchase is recorded
                against every page its buyer walked through, so a parent shows
                the largest single value beneath it rather than a total that
                would count the same person once per step. */}
            <span className="ml-2 text-xs text-muted-foreground">
              · Views total up; visitors, emails and payments show the largest value
              underneath, because one visitor is counted on every page they saw
            </span>
            <button 
              className="ml-4 text-xs text-primary hover:underline"
              onClick={() => setExpandedPaths(new Set(['/']))}
            >
              Collapse All
            </button>
            <button 
              className="ml-2 text-xs text-primary hover:underline"
              onClick={() => {
                // Expand all paths
                const allPaths = new Set(['/'])
                const collectPaths = (node) => {
                  if (node.hasChildren) {
                    allPaths.add(node.path)
                    node.children.forEach(collectPaths)
                  }
                }
                if (pageTree) collectPaths(pageTree)
                setExpandedPaths(allPaths)
              }}
            >
              Expand All
            </button>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pageLoading ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-12 bg-muted rounded animate-pulse"></div>
              ))}
            </div>
          ) : pageTree ? (
            <div className="overflow-x-auto">
              {/* Header */}
              <div className="grid grid-cols-[1fr_80px_80px_80px_80px_80px] gap-2 border-b pb-2 text-sm font-medium">
                <TrafficTreeSortHeaders nameLabel="Path" sort={pageSort} onSort={setPageSort} />
              </div>
              
              {/* Tree Rows */}
              <div className="mt-2">
                {/* Render root's children (skip root itself) */}
                {pageTree.children.map((child, idx) => (
                  <PageTreeNode 
                    key={child.path} 
                    node={child} 
                    depth={0} 
                    expandedPaths={expandedPaths}
                    togglePath={togglePath}
                    formatNumber={formatNumber}
                  />
                ))}
              </div>
              
              {/* Total row */}
              <div className="grid grid-cols-[1fr_80px_80px_80px_80px_80px] gap-2 border-t mt-2 pt-2 text-sm font-bold bg-muted/30 -mx-6 px-6 py-2">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  <span>Total</span>
                </div>
                <div className="text-right">{formatNumber(pageTree.pageviews)}</div>
                <div className="text-right">{formatNumber(pageTree.visitors)}</div>
                <div className="text-right">{formatNumber(pageTree.emails)}</div>
                <div className="text-right">{formatNumber(pageTree.payments)}</div>
                <div className="text-right" style={{ color: pageTree.cvr >= 10 ? '#10B981' : pageTree.cvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                  {pageTree.cvr?.toFixed(2) || 0}%
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No page data available for the selected period
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Quality Tab */}
      {activeTab === 'quality' && (
      <>
        {/* ── Traffic Quality Score ──────────────────────────────── */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base font-medium">Traffic Quality Score</CardTitle>
                <CardDescription>
                  Scores traffic by how deep it gets on the site, not just whether it converted.
                  Each session is worth the observed probability that visitors who got that deep
                  went on to pay — so the score reads as expected conversion %, and a bounce off
                  the LP is worth far less than a payment.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={scoreGroupBy} onValueChange={setScoreGroupBy}>
                  <SelectTrigger className="h-8 w-[150px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="source">By source</SelectItem>
                    <SelectItem value="sub_source">By sub-source</SelectItem>
                    <SelectItem value="landing_page">By landing page</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={scoreInterval} onValueChange={setScoreInterval}>
                  <SelectTrigger className="h-8 w-[100px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {scoreLoading ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-10 bg-muted rounded animate-pulse"></div>
                ))}
              </div>
            ) : scoreData?.scores?.length > 0 ? (
              <>
                {/* Headline */}
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-4">
                  <div>
                    <div className="text-2xl font-semibold">
                      {(scoreData.totals?.quality_score || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Overall score (expected CVR %)
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-medium">{formatNumber(scoreData.totals?.sessions || 0)}</div>
                    <div className="text-xs text-muted-foreground">Sessions scored</div>
                  </div>
                  <div>
                    <div className="text-lg font-medium">{scoreData.totals?.groups || 0}</div>
                    <div className="text-xs text-muted-foreground">
                      Groups (min 25 sessions)
                    </div>
                  </div>
                </div>

                {/* Depth legend */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[11px] text-muted-foreground">
                  {visibleDepths.map(m => (
                    <span key={m.key} className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-2 w-2 rounded-sm"
                        style={{ backgroundColor: DEPTH_COLORS[m.key] }}
                      />
                      {m.label}
                    </span>
                  ))}
                </div>

                {/* Score over time */}
                {scoreTrend?.series?.length > 0 && (
                  <div className="mb-5">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      Quality over time — weights are calibrated once across the whole date range,
                      so movement here means the traffic mix actually changed.
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={scoreTrend.series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                        />
                        <Tooltip
                          contentStyle={{ fontSize: 12 }}
                          formatter={(value, name) => [Number(value).toFixed(2), name]}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {(scoreTrend.groups || []).map((g, i) => (
                          <Line
                            key={g}
                            type="monotone"
                            dataKey={g}
                            name={g}
                            stroke={g === '(all traffic)' ? '#64748B' : scoreGroupBy === 'source' ? getSourceColor(g, i) : SCORE_LINE_COLORS[i % SCORE_LINE_COLORS.length]}
                            strokeWidth={g === '(all traffic)' ? 2.5 : 1.5}
                            strokeDasharray={g === '(all traffic)' ? '5 4' : undefined}
                            dot={false}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Leaderboard */}
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-[1fr_60px_55px_150px_55px_55px_50px] gap-2 border-b pb-2 text-xs font-medium text-muted-foreground">
                    <div>{scoreGroupBy === 'landing_page' ? 'Landing page' : scoreGroupBy === 'sub_source' ? 'Sub-source' : 'Source'}</div>
                    <div className="text-right">Score</div>
                    <div className="text-right" title="100 = brand average for this window">vs Avg</div>
                    <div>Depth mix</div>
                    <div className="text-right" title="Sessions that never left the landing page">Bounce</div>
                    <div className="text-right">Sessions</div>
                    <div className="text-right">CVR</div>
                  </div>
                  {scoreData.scores.map(row => (
                    <div
                      key={row.group_key}
                      className="grid grid-cols-[1fr_60px_55px_150px_55px_55px_50px] gap-2 items-center border-b border-border/50 py-2 text-xs hover:bg-muted/40"
                    >
                      <div className="truncate font-medium" title={row.group_key}>{row.group_key}</div>
                      <div className="text-right font-semibold tabular-nums">{row.quality_score.toFixed(2)}</div>
                      <div className={`text-right tabular-nums font-medium ${indexTone(row.quality_index)}`}>
                        {Math.round(row.quality_index)}
                      </div>
                      <div><DepthMixBar mix={row.mix} milestones={visibleDepths} /></div>
                      <div className="text-right tabular-nums text-muted-foreground">{row.bounce_pct.toFixed(0)}%</div>
                      <div className="text-right tabular-nums text-muted-foreground">{formatNumber(row.sessions)}</div>
                      <div className="text-right tabular-nums text-muted-foreground">{row.cvr.toFixed(1)}%</div>
                    </div>
                  ))}
                </div>

                {/* Weight transparency — the score is only trustworthy if you can
                    see the yardstick it was measured against. */}
                <button
                  onClick={() => setShowScoreWeights(v => !v)}
                  className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {showScoreWeights ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  How the score is calibrated
                </button>
                {showScoreWeights && (
                  <div className="mt-2 rounded-md border p-3">
                    <div className="text-xs text-muted-foreground mb-2">
                      Weights are measured from this brand's own data over {scoreData.dateRange?.start} → {scoreData.dateRange?.end},
                      pooled across all traffic and unaffected by the filters above. A session scores the
                      weight of the deepest milestone it reached.
                    </div>
                    <div className="grid grid-cols-[1fr_70px_90px_90px] gap-2 border-b pb-1 text-[11px] font-medium text-muted-foreground">
                      <div>Depth reached</div>
                      <div className="text-right">Weight</div>
                      <div className="text-right">Sessions ≥ here</div>
                      <div className="text-right">Stopped here</div>
                    </div>
                    {(scoreData.weights || []).map(w => (
                      <div key={w.key} className={`grid grid-cols-[1fr_70px_90px_90px] gap-2 py-1 text-[11px] ${w.observed ? '' : 'opacity-50'}`}>
                        <div className="inline-flex items-center gap-1.5">
                          <span
                            className="inline-block h-2 w-2 rounded-sm"
                            style={{ backgroundColor: DEPTH_COLORS[w.key] }}
                          />
                          {w.label}
                          {!w.observed && (
                            <span className="text-muted-foreground">— fires with the next step, not its own stage</span>
                          )}
                        </div>
                        <div className="text-right tabular-nums font-medium">{w.weight.toFixed(2)}</div>
                        <div className="text-right tabular-nums text-muted-foreground">{formatNumber(w.sessionsReached)}</div>
                        <div className="text-right tabular-nums text-muted-foreground">{formatNumber(w.sessionsAtLevel)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No scored traffic for this range and filter.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryCard
            title="Avg Events"
            value={qualityData?.totals?.avg_event_count?.toFixed(1) || '0'}
            icon={Zap}
            loading={qualityLoading}
            color="#8B5CF6"
          />
          <SummaryCard
            title="Sessions"
            value={formatNumber(qualityData?.totals?.unique_sessions || 0)}
            icon={Users}
            loading={qualityLoading}
            color="#3B82F6"
          />
          <SummaryCard
            title="→Intake%"
            value={`${(qualityData?.totals?.click_to_intake_rate || 0).toFixed(1)}%`}
            icon={TrendingUp}
            loading={qualityLoading}
            color="#10B981"
          />
          <SummaryCard
            title="CVR"
            value={`${(qualityData?.totals?.cvr || 0).toFixed(1)}%`}
            icon={TrendingUp}
            loading={qualityLoading}
            color="#F59E0B"
          />
        </div>

        {/* Quality Metrics Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Traffic Quality by Source</CardTitle>
            <CardDescription>
              Engagement and conversion metrics by traffic source
            </CardDescription>
          </CardHeader>
          <CardContent>
            {qualityLoading ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-10 bg-muted rounded animate-pulse"></div>
                ))}
              </div>
            ) : qualityData?.qualityMetrics?.length > 0 ? (
              <div className="overflow-x-auto">
                {/* Header */}
                {(() => {
                  const handleSort = (column) => {
                    if (qualitySortColumn === column) {
                      setQualitySortDirection(qualitySortDirection === 'desc' ? 'asc' : 'desc')
                    } else {
                      setQualitySortColumn(column)
                      setQualitySortDirection('desc')
                    }
                  }
                  const SortIndicator = ({ column }) => (
                    qualitySortColumn === column ? (
                      <span className="ml-0.5 text-[10px]">{qualitySortDirection === 'desc' ? '▼' : '▲'}</span>
                    ) : null
                  )
                  return (
                    <div className="grid grid-cols-[1fr_50px_55px_45px_45px_40px_45px_40px_40px_40px] gap-1 border-b pb-2 text-xs font-medium text-muted-foreground">
                      <div>Source / Campaign</div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('unique_sessions')}>
                        Sessions<SortIndicator column="unique_sessions" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-40 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          Total unique visitor sessions.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('avg_event_count')}>
                        Avg Events<SortIndicator column="avg_event_count" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-48 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          Average page views and events per session. Higher = more engaged.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('click_to_intake_rate')}>
                        Intake %<SortIndicator column="click_to_intake_rate" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-52 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          % of visitors who reached the intake form (excludes sessions starting on intake).
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('plan_selected_count')}>
                        Plans<SortIndicator column="plan_selected_count" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-40 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          Users who selected a subscription plan.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('click_to_plan_rate')}>
                        Plan %<SortIndicator column="click_to_plan_rate" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-44 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          % of intake visitors who selected a plan.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('payment_initiated_count')}>
                        Pay Init<SortIndicator column="payment_initiated_count" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-40 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          Users who initiated payment.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('init_rate')}>
                        Init %<SortIndicator column="init_rate" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-48 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          % of plan selectors who initiated payment.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('payment_succeeded_count')}>
                        Paid<SortIndicator column="payment_succeeded_count" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-36 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          Completed successful payments.
                        </div>
                      </div>
                      <div className="text-right group relative cursor-pointer hover:text-foreground" onClick={() => handleSort('cvr')}>
                        CVR<SortIndicator column="cvr" />
                        <div className="absolute hidden group-hover:block right-0 top-full mt-1 z-50 w-44 p-2 text-xs font-normal bg-popover text-popover-foreground border rounded-md shadow-lg">
                          Conversion rate: payments / sessions.
                        </div>
                      </div>
                    </div>
                  )
                })()}

                {/* Data rows grouped by source → sub_source → sub1 */}
                <div className="mt-2 space-y-1">
                  {(() => {
                    // Build 3-level hierarchy: source → sub_source → sub1
                    const bySource = {}
                    qualityData.qualityMetrics.forEach(row => {
                      const sourceId = row.source
                      const subSourceId = row.sub_source || 'direct'
                      const sub1Id = row.sub1 || null

                      // Initialize source
                      if (!bySource[sourceId]) {
                        bySource[sourceId] = {
                          source: sourceId,
                          subSources: {},
                          totals: { avg_event_count: 0, unique_sessions: 0, intake_visitors: 0, plan_selected_count: 0, payment_initiated_count: 0, payment_succeeded_count: 0 }
                        }
                      }

                      // Initialize sub_source
                      if (!bySource[sourceId].subSources[subSourceId]) {
                        bySource[sourceId].subSources[subSourceId] = {
                          sub_source: subSourceId,
                          sub1s: {},
                          totals: { avg_event_count: 0, unique_sessions: 0, intake_visitors: 0, plan_selected_count: 0, payment_initiated_count: 0, payment_succeeded_count: 0 }
                        }
                      }

                      // Add sub1 leaf or aggregate to sub_source if no sub1
                      if (sub1Id) {
                        bySource[sourceId].subSources[subSourceId].sub1s[sub1Id] = {
                          sub1: sub1Id,
                          avg_event_count: row.avg_event_count || 0,
                          unique_sessions: row.unique_sessions || 0,
                          intake_visitors: row.intake_visitors || 0,
                          click_to_intake_rate: row.click_to_intake_rate || 0,
                          plan_selected_count: row.plan_selected_count || 0,
                          click_to_plan_rate: row.click_to_plan_rate || 0,
                          payment_initiated_count: row.payment_initiated_count || 0,
                          payment_succeeded_count: row.payment_succeeded_count || 0,
                          cvr: row.cvr || 0
                        }
                      }

                      // Aggregate to sub_source totals
                      const ss = bySource[sourceId].subSources[subSourceId]
                      ss.totals.avg_event_count += (row.avg_event_count || 0) * (row.unique_sessions || 0)
                      ss.totals.unique_sessions += row.unique_sessions || 0
                      ss.totals.intake_visitors += row.intake_visitors || 0
                      ss.totals.plan_selected_count += row.plan_selected_count || 0
                      ss.totals.payment_initiated_count += row.payment_initiated_count || 0
                      ss.totals.payment_succeeded_count += row.payment_succeeded_count || 0

                      // Store raw row for sub_sources without sub1 children
                      if (!sub1Id) {
                        ss.rawRow = row
                      }

                      // Aggregate to source totals
                      const src = bySource[sourceId]
                      src.totals.avg_event_count += (row.avg_event_count || 0) * (row.unique_sessions || 0)
                      src.totals.unique_sessions += row.unique_sessions || 0
                      src.totals.intake_visitors += row.intake_visitors || 0
                      src.totals.plan_selected_count += row.plan_selected_count || 0
                      src.totals.payment_initiated_count += row.payment_initiated_count || 0
                      src.totals.payment_succeeded_count += row.payment_succeeded_count || 0
                    })

                    // Sort helper function
                    const getSortValue = (totals, column) => {
                      const t = totals
                      switch (column) {
                        case 'unique_sessions': return t.unique_sessions || 0
                        case 'avg_event_count': return t.unique_sessions > 0 ? t.avg_event_count / t.unique_sessions : 0
                        case 'click_to_intake_rate': return t.unique_sessions > 0 ? t.intake_visitors / t.unique_sessions : 0
                        case 'plan_selected_count': return t.plan_selected_count || 0
                        case 'click_to_plan_rate': return t.intake_visitors > 0 ? t.plan_selected_count / t.intake_visitors : 0
                        case 'payment_initiated_count': return t.payment_initiated_count || 0
                        case 'init_rate': return t.plan_selected_count > 0 ? t.payment_initiated_count / t.plan_selected_count : 0
                        case 'payment_succeeded_count': return t.payment_succeeded_count || 0
                        case 'cvr': return t.unique_sessions > 0 ? t.payment_succeeded_count / t.unique_sessions : 0
                        default: return t.unique_sessions || 0
                      }
                    }
                    const sortMultiplier = qualitySortDirection === 'desc' ? 1 : -1

                    // Convert to sorted arrays
                    const sources = Object.values(bySource)
                      .sort((a, b) => sortMultiplier * (getSortValue(b.totals, qualitySortColumn) - getSortValue(a.totals, qualitySortColumn)))
                      .map(src => ({
                        ...src,
                        subSourcesList: Object.values(src.subSources)
                          .sort((a, b) => sortMultiplier * (getSortValue(b.totals, qualitySortColumn) - getSortValue(a.totals, qualitySortColumn)))
                          .map(ss => ({
                            ...ss,
                            sub1sList: Object.values(ss.sub1s).sort((a, b) => {
                              const aVal = getSortValue({ ...a, avg_event_count: a.avg_event_count * a.unique_sessions }, qualitySortColumn)
                              const bVal = getSortValue({ ...b, avg_event_count: b.avg_event_count * b.unique_sessions }, qualitySortColumn)
                              return sortMultiplier * (bVal - aVal)
                            })
                          }))
                      }))

                    return sources.map(group => {
                      const isSourceExpanded = expandedQualitySources.has(group.source)
                      const t = group.totals
                      const avgEvents = t.unique_sessions > 0 ? t.avg_event_count / t.unique_sessions : 0
                      const intakeRate = t.unique_sessions > 0 ? 100 * t.intake_visitors / t.unique_sessions : 0
                      const planRate = t.intake_visitors > 0 ? 100 * t.plan_selected_count / t.intake_visitors : 0
                      const initRate = t.plan_selected_count > 0 ? 100 * t.payment_initiated_count / t.plan_selected_count : 0
                      const cvr = t.unique_sessions > 0 ? 100 * t.payment_succeeded_count / t.unique_sessions : 0
                      const hasChildren = group.subSourcesList.length > 0

                      return (
                        <div key={group.source}>
                          {/* Source row */}
                          <div
                            className="grid grid-cols-[1fr_50px_55px_45px_45px_40px_45px_40px_40px_40px] gap-1 py-1.5 text-sm hover:bg-muted/30 rounded cursor-pointer border-b border-muted/30"
                            onClick={() => {
                              const newSet = new Set(expandedQualitySources)
                              if (isSourceExpanded) newSet.delete(group.source)
                              else newSet.add(group.source)
                              setExpandedQualitySources(newSet)
                            }}
                          >
                            <div className="flex items-center gap-1 font-medium">
                              {hasChildren ? (
                                isSourceExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                              ) : <span className="w-4" />}
                              <span className="capitalize">{group.source}</span>
                              <span className="text-muted-foreground text-xs">({group.subSourcesList.length})</span>
                            </div>
                            <div className="text-right text-xs">{formatNumber(t.unique_sessions)}</div>
                            <div className="text-right text-xs" style={{ color: avgEvents >= 5 ? '#10B981' : avgEvents >= 3 ? '#F59E0B' : '#6B7280' }}>{avgEvents.toFixed(1)}</div>
                            <div className="text-right text-xs" style={{ color: intakeRate >= 70 ? '#10B981' : intakeRate >= 50 ? '#F59E0B' : '#6B7280' }}>{intakeRate.toFixed(0)}%</div>
                            <div className="text-right text-xs">{formatNumber(t.plan_selected_count)}</div>
                            <div className="text-right text-xs" style={{ color: planRate >= 50 ? '#10B981' : planRate >= 30 ? '#F59E0B' : '#6B7280' }}>{planRate.toFixed(0)}%</div>
                            <div className="text-right text-xs">{formatNumber(t.payment_initiated_count)}</div>
                            <div className="text-right text-xs" style={{ color: initRate >= 80 ? '#10B981' : initRate >= 60 ? '#F59E0B' : '#6B7280' }}>{initRate.toFixed(0)}%</div>
                            <div className="text-right text-xs">{formatNumber(t.payment_succeeded_count)}</div>
                            <div className="text-right text-xs font-medium" style={{ color: cvr >= 10 ? '#10B981' : cvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                              {cvr.toFixed(1)}%
                            </div>
                          </div>

                          {/* Sub-source rows (level 2) */}
                          {isSourceExpanded && group.subSourcesList.map(ss => {
                            const ssKey = `${group.source}-${ss.sub_source}`
                            const isSubSourceExpanded = expandedQualitySubSources.has(ssKey)
                            const hasSub1Children = ss.sub1sList.length > 0
                            const sst = ss.totals
                            const ssAvgEvents = sst.unique_sessions > 0 ? sst.avg_event_count / sst.unique_sessions : 0
                            const ssIntakeRate = sst.unique_sessions > 0 ? 100 * sst.intake_visitors / sst.unique_sessions : 0
                            const ssPlanRate = sst.intake_visitors > 0 ? 100 * sst.plan_selected_count / sst.intake_visitors : 0
                            const ssInitRate = sst.plan_selected_count > 0 ? 100 * sst.payment_initiated_count / sst.plan_selected_count : 0
                            const ssCvr = sst.unique_sessions > 0 ? 100 * sst.payment_succeeded_count / sst.unique_sessions : 0

                            return (
                              <div key={ssKey}>
                                {/* Sub-source row */}
                                <div
                                  className={`grid grid-cols-[1fr_50px_55px_45px_45px_40px_45px_40px_40px_40px] gap-1 py-1 text-xs hover:bg-muted/20 rounded pl-6 ${hasSub1Children ? 'cursor-pointer' : ''}`}
                                  onClick={() => {
                                    if (hasSub1Children) {
                                      const newSet = new Set(expandedQualitySubSources)
                                      if (isSubSourceExpanded) newSet.delete(ssKey)
                                      else newSet.add(ssKey)
                                      setExpandedQualitySubSources(newSet)
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    {hasSub1Children ? (
                                      isSubSourceExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
                                    ) : <span className="w-3" />}
                                    <span className="truncate" title={ss.sub_source}>
                                      {decodeUTM(ss.sub_source)}
                                    </span>
                                    {hasSub1Children && <span className="text-muted-foreground/60 text-[10px]">({ss.sub1sList.length})</span>}
                                  </div>
                                  <div className="text-right">{formatNumber(sst.unique_sessions)}</div>
                                  <div className="text-right" style={{ color: ssAvgEvents >= 5 ? '#10B981' : ssAvgEvents >= 3 ? '#F59E0B' : '#6B7280' }}>{ssAvgEvents.toFixed(1)}</div>
                                  <div className="text-right" style={{ color: ssIntakeRate >= 70 ? '#10B981' : ssIntakeRate >= 50 ? '#F59E0B' : '#6B7280' }}>{ssIntakeRate.toFixed(0)}%</div>
                                  <div className="text-right">{formatNumber(sst.plan_selected_count)}</div>
                                  <div className="text-right" style={{ color: ssPlanRate >= 50 ? '#10B981' : ssPlanRate >= 30 ? '#F59E0B' : '#6B7280' }}>{ssPlanRate.toFixed(0)}%</div>
                                  <div className="text-right">{formatNumber(sst.payment_initiated_count)}</div>
                                  <div className="text-right" style={{ color: ssInitRate >= 80 ? '#10B981' : ssInitRate >= 60 ? '#F59E0B' : '#6B7280' }}>{ssInitRate.toFixed(0)}%</div>
                                  <div className="text-right">{formatNumber(sst.payment_succeeded_count)}</div>
                                  <div className="text-right" style={{ color: ssCvr >= 10 ? '#10B981' : ssCvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                                    {ssCvr.toFixed(1)}%
                                  </div>
                                </div>

                                {/* Sub1 rows (level 3) */}
                                {isSubSourceExpanded && ss.sub1sList.map(sub1Row => {
                                  const s1Events = sub1Row.avg_event_count || 0
                                  const s1Intake = sub1Row.click_to_intake_rate || 0
                                  const s1Plan = sub1Row.click_to_plan_rate || 0
                                  const s1Init = (sub1Row.plan_selected_count || 0) > 0 ? 100 * (sub1Row.payment_initiated_count || 0) / sub1Row.plan_selected_count : 0
                                  const s1Cvr = sub1Row.cvr || 0
                                  return (
                                    <div
                                      key={`${ssKey}-${sub1Row.sub1}`}
                                      className="grid grid-cols-[1fr_50px_55px_45px_45px_40px_45px_40px_40px_40px] gap-1 py-1 text-xs text-muted-foreground/70 hover:bg-muted/10 rounded pl-12"
                                    >
                                      <div className="truncate" title={sub1Row.sub1}>
                                        <span className="text-muted-foreground/50">└</span> {decodeUTM(sub1Row.sub1)}
                                      </div>
                                      <div className="text-right">{formatNumber(sub1Row.unique_sessions || 0)}</div>
                                      <div className="text-right" style={{ color: s1Events >= 5 ? '#10B981' : s1Events >= 3 ? '#F59E0B' : '#6B7280' }}>{s1Events.toFixed(1)}</div>
                                      <div className="text-right" style={{ color: s1Intake >= 70 ? '#10B981' : s1Intake >= 50 ? '#F59E0B' : '#6B7280' }}>{s1Intake.toFixed(0)}%</div>
                                      <div className="text-right">{formatNumber(sub1Row.plan_selected_count || 0)}</div>
                                      <div className="text-right" style={{ color: s1Plan >= 50 ? '#10B981' : s1Plan >= 30 ? '#F59E0B' : '#6B7280' }}>{s1Plan.toFixed(0)}%</div>
                                      <div className="text-right">{formatNumber(sub1Row.payment_initiated_count || 0)}</div>
                                      <div className="text-right" style={{ color: s1Init >= 80 ? '#10B981' : s1Init >= 60 ? '#F59E0B' : '#6B7280' }}>{s1Init.toFixed(0)}%</div>
                                      <div className="text-right">{formatNumber(sub1Row.payment_succeeded_count || 0)}</div>
                                      <div className="text-right" style={{ color: s1Cvr >= 10 ? '#10B981' : s1Cvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                                        {s1Cvr.toFixed(1)}%
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })
                  })()}
                </div>

                {/* Totals row */}
                {(() => {
                  const tEvents = qualityData.totals?.avg_event_count || 0
                  const tIntake = qualityData.totals?.click_to_intake_rate || 0
                  const tPlan = qualityData.totals?.click_to_plan_rate || 0
                  const tInit = (qualityData.totals?.plan_selected_count || 0) > 0 ? 100 * (qualityData.totals?.payment_initiated_count || 0) / qualityData.totals.plan_selected_count : 0
                  const tCvr = qualityData.totals?.cvr || 0
                  return (
                    <div className="grid grid-cols-[1fr_50px_55px_45px_45px_40px_45px_40px_40px_40px] gap-1 border-t mt-2 pt-2 text-sm font-bold bg-muted/30 -mx-6 px-6 py-2">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        <span>Total</span>
                      </div>
                      <div className="text-right text-xs">{formatNumber(qualityData.totals?.unique_sessions || 0)}</div>
                      <div className="text-right text-xs" style={{ color: tEvents >= 5 ? '#10B981' : tEvents >= 3 ? '#F59E0B' : '#6B7280' }}>{tEvents.toFixed(1)}</div>
                      <div className="text-right text-xs" style={{ color: tIntake >= 70 ? '#10B981' : tIntake >= 50 ? '#F59E0B' : '#6B7280' }}>{tIntake.toFixed(0)}%</div>
                      <div className="text-right text-xs">{formatNumber(qualityData.totals?.plan_selected_count || 0)}</div>
                      <div className="text-right text-xs" style={{ color: tPlan >= 50 ? '#10B981' : tPlan >= 30 ? '#F59E0B' : '#6B7280' }}>{tPlan.toFixed(0)}%</div>
                      <div className="text-right text-xs">{formatNumber(qualityData.totals?.payment_initiated_count || 0)}</div>
                      <div className="text-right text-xs" style={{ color: tInit >= 80 ? '#10B981' : tInit >= 60 ? '#F59E0B' : '#6B7280' }}>{tInit.toFixed(0)}%</div>
                      <div className="text-right text-xs">{formatNumber(qualityData.totals?.payment_succeeded_count || 0)}</div>
                      <div className="text-right text-xs" style={{ color: tCvr >= 10 ? '#10B981' : tCvr >= 5 ? '#F59E0B' : '#6B7280' }}>
                        {tCvr.toFixed(1)}%
                      </div>
                    </div>
                  )
                })()}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No quality data available for the selected period
              </div>
            )}
          </CardContent>
        </Card>
      </>
      )}

      {/* Duration Trends Tab */}
      {activeTab === 'steps' && (
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between space-y-0">
            <div>
              <CardTitle>Intake Steps</CardTitle>
              <CardDescription>
                Every screen of one intake funnel, in order. A person counts at a step only if they reached
                every step before it, in order, within {screenSteps?.window_days ?? 7} days of the first — the same
                rule as the PostHog "Intake funnel — step by step" chart. Time is the median from the previous step.
              </CardDescription>
              {/* Said outright: Segment loads BigQuery in batches, so "today" is
                  an hour or two behind and must not be read as live. */}
              {(screenSteps?.data_current_through || screenSteps?.learned_from) && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {formatCurrentThrough(screenSteps.data_current_through) && (
                    <>Data current through <span className="font-medium text-foreground">{formatCurrentThrough(screenSteps.data_current_through)}</span> (Segment loads run an hour or two behind). </>
                  )}
                  {formatDayRange(screenSteps.learned_from) && (
                    <>Steps and funnel visitor counts learned from {formatDayRange(screenSteps.learned_from)}; step counts cover only the dates selected.</>
                  )}
                </p>
              )}
            </div>
            {screenSteps?.funnels?.length > 0 && (
              <Select value={screenSteps.funnel} onValueChange={setScreenFunnel}>
                <SelectTrigger className="w-56 h-8 text-sm">
                  <SelectValue placeholder="Funnel" />
                </SelectTrigger>
                <SelectContent>
                  {screenSteps.funnels.map(f => (
                    <SelectItem key={f.funnel} value={f.funnel}>
                      /{f.funnel} <span className="text-muted-foreground">· {formatNumber(f.visitors)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardHeader>
          <CardContent>
            {screenStepsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading intake steps...</span>
              </div>
            ) : screenStepsError ? (
              <div className="py-12 text-center text-muted-foreground">{screenStepsError}</div>
            ) : !screenSteps?.steps?.length ? (
              <div className="py-12 text-center text-muted-foreground">
                No intake screens tracked for this clinic and date range.
              </div>
            ) : (() => {
              const steps = screenSteps.steps
              const leak = biggestLeakStep(steps)
              return (
                <>
                  {screenSteps.path_basis === 'visitors' && (
                    <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                      Fewer than 10 people reached payment even over the last 28 days, so the steps were worked out
                      from all visitors. If this intake branches, later steps may read low.
                    </div>
                  )}
                  <div className="grid grid-cols-[2rem_minmax(8rem,12rem)_1fr_5rem_4.5rem_4.5rem_4.5rem] gap-x-3 gap-y-1.5 items-center text-sm">
                    <div />
                    <div className="text-xs text-muted-foreground">Step</div>
                    <div className="text-xs text-muted-foreground">Reached (of first step)</div>
                    <div className="text-xs text-muted-foreground text-right">People</div>
                    <div className="text-xs text-muted-foreground text-right" title="People at this step ÷ people at the previous step">Step conv.</div>
                    <div className="text-xs text-muted-foreground text-right" title="Share of the previous step's people who did not reach this one">Drop-off</div>
                    <div className="text-xs text-muted-foreground text-right" title="Median time from the previous step">Time</div>
                    {steps.map(step => {
                      const isLeak = step.step_id === leak
                      return (
                        <div key={step.step_id} className="contents">
                          <div className="text-xs text-muted-foreground tabular-nums">{step.step_id}</div>
                          <div className={`truncate ${step.kind === 'event' ? 'font-semibold' : ''}`} title={step.key}>
                            {screenLabel(step)}
                            {screenLifetimeNote(step, screenSteps.screen_availability) && (
                              <span
                                className="ml-1.5 rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-200"
                                title={screenLifetimeNote(step, screenSteps.screen_availability).title}
                              >
                                {screenLifetimeNote(step, screenSteps.screen_availability).badge}
                              </span>
                            )}
                          </div>
                          <div className="h-5 rounded bg-muted overflow-hidden">
                            <div
                              className={`h-full ${step.kind === 'event' ? 'bg-emerald-500' : 'bg-primary'}`}
                              style={{ width: `${Math.min(100, (step.pct_of_first ?? 0) * 100)}%` }}
                              title={formatPct(step.pct_of_first)}
                            />
                          </div>
                          <div className="text-right tabular-nums">{step.people.toLocaleString()}</div>
                          <div className="text-right tabular-nums">{formatPct(step.pct_of_previous)}</div>
                          <div className={`text-right tabular-nums ${isLeak ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}`}>
                            {formatPct(step.dropoff_from_previous)}
                          </div>
                          <div className="text-right tabular-nums text-muted-foreground" title={`Average ${formatDuration(step.avg_secs_from_previous)}`}>
                            {formatDuration(step.p50_secs_from_previous)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground">
                    {formatPct(steps[steps.length - 1].pct_of_first)} of people who reached the first screen paid.
                    The biggest leak is highlighted. Screens only some people see (branches) and screens after payment
                    are left out; the steps are the ones people who reached payment walked.
                    {singleSource && ' Filtered to the source chosen above, by first touch.'}
                  </p>
                </>
              )
            })()}
          </CardContent>
        </Card>
      )}

      {activeTab === 'durations' && (
      <>
        {durationLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading funnel durations...</span>
            </CardContent>
          </Card>
        ) : !durationData ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No duration data available for this date range.
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Percentile Toggle */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">Percentile:</span>
              <button
                onClick={() => setDurationPercentile('p50')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  durationPercentile === 'p50'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                P50 (Median)
              </button>
              <button
                onClick={() => setDurationPercentile('p90')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  durationPercentile === 'p90'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                P90
              </button>
            </div>

            {/* Anomaly Alerts */}
            {durationData.anomalies?.alerts?.length > 0 && (
              <Card className="mb-4 border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground">
                    <AlertCircle className="h-4 w-4 inline-block mr-2 text-amber-600 dark:text-amber-400" />
                    Anomalies Detected ({durationData.anomalies.alerts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {durationData.anomalies.alerts.slice(0, 5).map((alert, i) => (
                      <div key={i} className="text-xs text-muted-foreground">
                        <span className="font-medium">{alert.metric.replace(/_/g, ' ')}</span>
                        {' '}in {alert.week}: {alert.value}s ({alert.pctChange > 0 ? '+' : ''}{alert.pctChange}% vs baseline {alert.baseline}s)
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Duration Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Funnel Step Durations ({durationPercentile.toUpperCase()}) — Weekly</CardTitle>
                <CardDescription>Time between acquisition funnel steps in seconds, trended by ISO week</CardDescription>
              </CardHeader>
              <CardContent>
                {durationData.weeks?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={durationData.weeks}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="iso_week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: 'Seconds', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                        formatter={(value) => value != null ? `${value}s` : 'N/A'}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey={`${durationPercentile}_lp_to_intake`} name="LP → Intake" stroke="#8884d8" strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey={`${durationPercentile}_intake_to_email`} name="Intake → Email" stroke="#82ca9d" strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey={`${durationPercentile}_email_to_plan`} name="Email → Plan" stroke="#ffc658" strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey={`${durationPercentile}_plan_to_payment`} name="Plan → Payment" stroke="#ff7c7c" strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey={`${durationPercentile}_lp_to_payment`} name="LP → Payment (E2E)" stroke="#8dd1e1" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No weekly data available</p>
                )}
              </CardContent>
            </Card>

            {/* Weekly Data Table */}
            {durationData.weeks?.length > 0 && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-base">Weekly Detail</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-medium">Week</th>
                          <th className="text-right py-2 px-2 font-medium">Users</th>
                          <th className="text-right py-2 px-2 font-medium">LP→Intake</th>
                          <th className="text-right py-2 px-2 font-medium">Intake→Email</th>
                          <th className="text-right py-2 px-2 font-medium">Email→Plan</th>
                          <th className="text-right py-2 px-2 font-medium">Plan→Payment</th>
                          <th className="text-right py-2 px-2 font-medium">LP→Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {durationData.weeks.map((w) => {
                          const hasAnomaly = (metric) =>
                            durationData.anomalies?.alerts?.some(a => a.week === w.iso_week && a.metric === metric)
                          return (
                            <tr key={w.iso_week} className="border-b border-border/50 hover:bg-muted/50">
                              <td className="py-1.5 px-2 font-mono">{w.iso_week}</td>
                              <td className="py-1.5 px-2 text-right">{w.users?.toLocaleString()}</td>
                              {['lp_to_intake', 'intake_to_email', 'email_to_plan', 'plan_to_payment', 'lp_to_payment'].map(step => {
                                const val = w[`${durationPercentile}_${step}`]
                                const anomaly = hasAnomaly(`${durationPercentile}_${step}`)
                                return (
                                  <td key={step} className={`py-1.5 px-2 text-right ${anomaly ? 'text-amber-600 font-medium' : ''}`}>
                                    {val != null ? `${val}s` : '—'}
                                    {anomaly && ' ⚠'}
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </>
      )}

      {/* Settings Tab (Admin Only) */}
      {activeTab === 'settings' && isAdmin && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Campaign Aliases */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Campaign Aliases</CardTitle>
            <CardDescription>Create friendly names for campaigns</CardDescription>
          </CardHeader>
          <CardContent>
            {aliasData?.aliases && Object.keys(aliasData.aliases).length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {Object.entries(aliasData.aliases).map(([rawName, alias]) => (
                  <div key={rawName} className="p-3 rounded-lg border bg-card">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{alias.name}</div>
                        <div className="text-xs text-muted-foreground truncate" title={rawName}>
                          {decodeURIComponent(rawName).slice(0, 50)}...
                        </div>
                        {alias.group && (
                          <Badge variant="secondary" className="text-xs mt-1">
                            {alias.group}
                          </Badge>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteAlias(rawName)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <p>No aliases configured yet.</p>
                <p className="text-xs mt-1">Click the edit button on campaigns to add aliases.</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Campaign Groups */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Campaign Groups</CardTitle>
            <CardDescription>Auto-group campaigns by pattern matching</CardDescription>
          </CardHeader>
          <CardContent>
            {aliasData?.groups?.length > 0 ? (
              <div className="space-y-2">
                {aliasData.groups.map((group) => (
                  <div key={group.id} className="p-3 rounded-lg border bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      <span className="font-medium text-sm">{group.name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {group.patterns?.map((pattern, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          *{pattern}*
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <p>No groups configured.</p>
                <p className="text-xs mt-1">Edit campaign-aliases.json to add groups.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Source Overrides Info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Source Configuration</CardTitle>
          <CardDescription>How campaigns are extracted for each source</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channelColor('meta') }} />
                <span className="font-medium text-sm">Meta Ads</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Uses <code className="bg-muted px-1 rounded">utm_content</code> for campaign ID (more granular), 
                falls back to <code className="bg-muted px-1 rounded">utm_campaign</code>
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channelColor('google') }} />
                <span className="font-medium text-sm">Google Ads</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Uses <code className="bg-muted px-1 rounded">utm_campaign</code> for campaign identification
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channelColor('everflow') }} />
                <span className="font-medium text-sm">Everflow</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Uses <code className="bg-muted px-1 rounded">affid</code> (affiliate ID) for sub-source
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channelColor('katalys') }} />
                <span className="font-medium text-sm">Katalys</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Uses <code className="bg-muted px-1 rounded">publisher_id</code> for affiliate identification
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Add Alias from Campaign List */}
      {subSourceData?.subSources?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Quick Add Aliases</CardTitle>
            <CardDescription>Click to add friendly names for raw campaign IDs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {subSourceData.subSources
                .filter(s => !aliasData?.aliases?.[s.sub_source])
                .slice(0, 20)
                .map((campaign, idx) => (
                  <div key={idx} className="p-3 rounded-lg border bg-card">
                    {editingAlias === campaign.sub_source ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: getSourceColor(campaign.source) }}
                          />
                          <span className="text-xs text-muted-foreground truncate">
                            {decodeURIComponent(campaign.sub_source).slice(0, 40)}...
                          </span>
                        </div>
                        <input
                          type="text"
                          placeholder="Friendly name..."
                          value={newAliasName}
                          onChange={(e) => setNewAliasName(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background"
                          autoFocus
                        />
                        <input
                          type="text"
                          placeholder="Group (optional)..."
                          value={newAliasGroup}
                          onChange={(e) => setNewAliasGroup(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background"
                        />
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleSaveAlias(campaign.sub_source)}
                            disabled={!newAliasName.trim()}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Save
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setEditingAlias(null)
                              setNewAliasName('')
                              setNewAliasGroup('')
                            }}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div 
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: getSourceColor(campaign.source) }}
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-mono truncate" title={campaign.sub_source}>
                              {decodeURIComponent(campaign.sub_source || 'direct').slice(0, 40)}
                              {campaign.sub_source?.length > 40 ? '...' : ''}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {getSourceName(campaign.source)} • {formatNumber(campaign.visitors)} visitors
                            </div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setEditingAlias(campaign.sub_source)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Alias
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  )
}
